import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Grid, Typography, Paper, Chip, Stack, Button, IconButton,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Alert, Tooltip, Divider, Switch, FormControlLabel,
  Tabs, Tab, Snackbar, Avatar, CircularProgress, alpha, Card, CardContent,
  useMediaQuery, useTheme,
} from '@mui/material';
import ChevronLeftIcon    from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon   from '@mui/icons-material/ChevronRight';
import AddIcon            from '@mui/icons-material/Add';
import EditIcon           from '@mui/icons-material/Edit';
import DeleteIcon         from '@mui/icons-material/DeleteOutline';
import EventBusyIcon      from '@mui/icons-material/EventBusy';
import BeachAccessIcon    from '@mui/icons-material/BeachAccess';
import BlockIcon          from '@mui/icons-material/Block';
import InfoOutlinedIcon   from '@mui/icons-material/InfoOutlined';
import DownloadIcon       from '@mui/icons-material/Download';
import SaveIcon           from '@mui/icons-material/Save';
import CheckCircleIcon    from '@mui/icons-material/CheckCircle';
import CancelIcon         from '@mui/icons-material/Cancel';
import HourglassTopIcon   from '@mui/icons-material/HourglassTop';
import FilterListIcon     from '@mui/icons-material/FilterList';
import CalendarMonthIcon  from '@mui/icons-material/CalendarMonth';
import WorkHistoryIcon    from '@mui/icons-material/WorkHistory';
import GridViewIcon       from '@mui/icons-material/GridView';
import PersonIcon         from '@mui/icons-material/Person';
import SearchIcon         from '@mui/icons-material/Search';
import FileDownloadIcon   from '@mui/icons-material/FileDownload';
import GroupsIcon         from '@mui/icons-material/Groups';
import LocalHospitalIcon  from '@mui/icons-material/LocalHospital';
import FavoriteIcon       from '@mui/icons-material/Favorite';
import XLSX               from 'xlsx-js-style';
import InputAdornment     from '@mui/material/InputAdornment';
import apiService, { authFetch } from '../../services/apiServices';
import Autocomplete from '@mui/material/Autocomplete';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import presenceService    from '../../services/presenceService';
import ShiftPlanning      from '../company/ShiftPlanning';
import { getUserDisplayName } from '../../services/authSession';
import { activeWorkers } from '../../utils/workerStatus';
// ─── constants ────────────────────────────────────────────────────────────────
const MONTHS_ALB = ['Janar','Shkurt','Mars','Prill','Maj','Qershor','Korrik','Gusht','Shtator','Tetor','Nëntor','Dhjetor'];
const DAYS_SHORT  = ['Hë','Ma','Më','En','Pr','Sh','Di'];

const COLORS = {
  holiday:  { bg:'#FEE2E2', border:'#DC2626' },
  blackout: { bg:'#FEF3C7', border:'#D97706' },
  approved: { bg:'#D1FAE5', border:'#3E8FA3' },
  pending:  { bg:'#DBEAFE', border:'#2563EB' },
  rejected: { bg:'#F3F4F6', border:'#6B7280' },
  today:    { bg:'#EEF2FF', border:'#3E8FA3', text:'#3730A3' },
};

const BLACKOUT_TYPES = {
  finance:    { label:'Financiar',      color:'#D97706' },
  audit:      { label:'Audit',          color:'#256878' },
  operations: { label:'Operacional',    color:'#0891B2' },
  collective: { label:'Pushim kolektiv',color:'#3E8FA3' },
  other:      { label:'Tjetër',         color:'#6B7280' },
};

const STATUS_META = {
  pending:  { label:'Në pritje', color:'primary', icon:<HourglassTopIcon sx={{ fontSize:14 }} /> },
  approved: { label:'Aprovuar',  color:'success', icon:<CheckCircleIcon  sx={{ fontSize:14 }} /> },
  rejected: { label:'Refuzuar',  color:'error',   icon:<CancelIcon       sx={{ fontSize:14 }} /> },
};

// ─── helpers ──────────────────────────────────────────────────────────────────
const isoDate        = (y, m, d) => `${y}-${String(m+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
const daysInMonth    = (y, m)    => new Date(y, m+1, 0).getDate();
const firstDayOfMon  = (y, m)    => { const d = new Date(y,m,1).getDay(); return d===0?6:d-1; };
const isWeekend      = (y, m, d) => { const day = new Date(y,m,d).getDay(); return day===0||day===6; };
const dateInRange    = (date, from, to) => date >= from && date <= to;
const today          = new Date().toISOString().split('T')[0];
const fmtDate        = (d) => d ? d.split('-').reverse().join('/') : '—';
const workerInitials = (name='') => name.split(' ').map(n=>n[0]||'').join('').toUpperCase().slice(0,2)||'?';

// ─── CalendarDay ──────────────────────────────────────────────────────────────
function CalendarDay({ dateStr, day, holidays, blackouts, leaves, onDayClick }) {
  const dayHolidays  = holidays.filter(h => h.Date === dateStr);
  const dayBlackouts = blackouts.filter(b => dateInRange(dateStr, b.DateFrom, b.DateTo));
  const dayLeaves    = leaves.filter(l => dateInRange(dateStr, l.StartDate, l.EndDate));

  const isToday    = dateStr === today;
  const isHoliday  = dayHolidays.length > 0;
  const isBlackout = dayBlackouts.length > 0;
  const [y, m, d]  = dateStr.split('-').map(Number);
  const weekend    = isWeekend(y, m-1, d);
  const hasEvents  = dayHolidays.length || dayBlackouts.length || dayLeaves.length;

  // top accent colour
  let accentColor = 'transparent';
  if (isHoliday)       accentColor = COLORS.holiday.border;
  else if (isBlackout) accentColor = COLORS.blackout.border;
  else if (isToday)    accentColor = COLORS.today.border;

  const pendingCount  = dayLeaves.filter(l => l.Status === 'pending').length;
  const approvedCount = dayLeaves.filter(l => l.Status === 'approved').length;

  return (
    <Box
      onClick={() => hasEvents && onDayClick({ dateStr, dayHolidays, dayBlackouts, dayLeaves })}
      sx={{
        height: '100%', minHeight: 82, display: 'flex', flexDirection: 'column',
        bgcolor: isHoliday ? '#FEF2F2' : isBlackout ? '#FFFBEB' : weekend ? '#F9FAFB' : '#fff',
        cursor: hasEvents ? 'pointer' : 'default',
        borderTop: `3px solid ${accentColor}`,
        '&:hover': hasEvents ? { filter: 'brightness(0.96)' } : {},
        transition: 'filter .12s',
      }}
    >
      {/* Day number */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 0.6, pt: 0.4 }}>
        <Box sx={{
          width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: '50%',
          bgcolor: isToday ? COLORS.today.border : 'transparent',
        }}>
          <Typography sx={{
            fontSize: '0.72rem', fontWeight: isToday ? 800 : weekend ? 400 : 600, lineHeight: 1,
            color: isToday ? '#fff' : isHoliday ? COLORS.holiday.border : weekend ? '#94A3B8' : '#334155',
          }}>
            {day}
          </Typography>
        </Box>
      </Box>

      {/* Event pills */}
      <Stack spacing={0.25} sx={{ px: 0.5, pb: 0.5, flex: 1, justifyContent: 'flex-end' }}>
        {dayHolidays.slice(0, 1).map(h => (
          <Tooltip key={h.Id} title={h.Name} placement="top" disableInteractive>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px',
              bgcolor: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 0.6, px: 0.5, py: 0.15 }}>
              <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: COLORS.holiday.border, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '8px', fontWeight: 700, color: COLORS.holiday.border,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3 }}>
                {h.Name}
              </Typography>
            </Box>
          </Tooltip>
        ))}
        {dayBlackouts.slice(0, 1).map(b => (
          <Tooltip key={b.Id} title={`Bllokuar: ${b.Name}`} placement="top" disableInteractive>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px',
              bgcolor: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 0.6, px: 0.5, py: 0.15 }}>
              <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: COLORS.blackout.border, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '8px', fontWeight: 700, color: COLORS.blackout.border, lineHeight: 1.3 }}>
                Bllokuar
              </Typography>
            </Box>
          </Tooltip>
        ))}
        {approvedCount > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px',
            bgcolor: '#D1FAE5', border: `1px solid ${COLORS.approved.border}`, borderRadius: 0.6, px: 0.5, py: 0.15 }}>
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: COLORS.approved.border, flexShrink: 0 }} />
            <Typography sx={{ fontSize: '8px', fontWeight: 600, color: '#065F46', lineHeight: 1.3 }}>
              {approvedCount} aprovuar
            </Typography>
          </Box>
        )}
        {pendingCount > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px',
            bgcolor: '#DBEAFE', border: `1px solid ${COLORS.pending.border}`, borderRadius: 0.6, px: 0.5, py: 0.15 }}>
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: COLORS.pending.border, flexShrink: 0 }} />
            <Typography sx={{ fontSize: '8px', fontWeight: 600, color: '#1E40AF', lineHeight: 1.3 }}>
              {pendingCount} pritje
            </Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
}

// ─── MonthView ────────────────────────────────────────────────────────────────
function MonthView({ year, month, holidays, blackouts, leaves, onDayClick }) {
  const firstDay = firstDayOfMon(year, month);
  const days     = daysInMonth(year, month);
  const cells    = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= days; d++)   cells.push(d);
  while (cells.length % 7 !== 0)    cells.push(null);

  const DAY_HEADERS = ['Hënë', 'Martë', 'Mërkurë', 'Enjte', 'Premte', 'Shtunë', 'Diel'];

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
      {/* Day headers */}
      {DAY_HEADERS.map((d, i) => (
        <Box key={d} sx={{
          py: 0.75, textAlign: 'center',
          bgcolor: i >= 5 ? '#FEF2F2' : '#F8FAFC',
          borderBottom: '1px solid', borderRight: i < 6 ? '1px solid' : 'none', borderColor: 'divider',
        }}>
          <Typography sx={{ fontSize: '0.67rem', fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: 0.4, color: i >= 5 ? '#DC2626' : '#64748B' }}>
            {d}
          </Typography>
        </Box>
      ))}
      {/* Day cells */}
      {cells.map((day, idx) => {
        const col = idx % 7;
        const isLastRow = idx >= cells.length - 7;
        return (
          <Box key={idx} sx={{
            borderBottom: isLastRow ? 'none' : '1px solid',
            borderRight: col < 6 ? '1px solid' : 'none',
            borderColor: 'divider',
            bgcolor: !day ? '#F8FAFC' : undefined,
          }}>
            {day
              ? <CalendarDay dateStr={isoDate(year, month, day)} day={day}
                  holidays={holidays} blackouts={blackouts} leaves={leaves} onDayClick={onDayClick} />
              : <Box sx={{ minHeight: 82 }} />}
          </Box>
        );
      })}
    </Box>
  );
}

// ─── RequestRow ───────────────────────────────────────────────────────────────
function RequestRow({ req, leaveTypes, onApprove, onReject, onDetail, saving }) {
  const type  = leaveTypes.find(t => t.Id === req.LeaveTypeId);
  const meta  = STATUS_META[req.Status] || STATUS_META.pending;
  return (
    <TableRow hover>
      <TableCell sx={{ py:0.75 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Avatar sx={{ width:28, height:28, fontSize:11, bgcolor:'primary.main' }}>{workerInitials(req.WorkerName)}</Avatar>
          <Box>
            <Typography variant="body2" fontWeight={600} sx={{ lineHeight:1.2 }}>{req.WorkerName}</Typography>
            <Typography variant="caption" color="text.secondary">{type?.Name || 'Pushim'}</Typography>
          </Box>
        </Stack>
      </TableCell>
      <TableCell sx={{ py:0.75 }}>
        <Typography variant="caption" fontWeight={600} sx={{ fontFamily:'monospace' }}>
          {fmtDate(req.StartDate)} → {fmtDate(req.EndDate)}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display:'block' }}>{req.DaysCount} ditë</Typography>
      </TableCell>
      <TableCell sx={{ py:0.75 }}>
        <Chip size="small" label={meta.label} color={meta.color} icon={meta.icon} variant="outlined" sx={{ fontSize:10 }} />
      </TableCell>
      <TableCell align="right" sx={{ py:0.75, pr:1 }}>
        {req.Status === 'pending' ? (
          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
            <Tooltip title="Aprovo">
              <span>
                <IconButton size="small" color="success" onClick={() => onApprove(req)} disabled={saving}>
                  {saving ? <CircularProgress size={14} /> : <CheckCircleIcon sx={{ fontSize:16 }} />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Refuzo">
              <span>
                <IconButton size="small" color="error" onClick={() => onReject(req)} disabled={saving}>
                  <CancelIcon sx={{ fontSize:16 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        ) : (
          <IconButton size="small" onClick={() => onDetail(req)}><InfoOutlinedIcon sx={{ fontSize:15 }} /></IconButton>
        )}
      </TableCell>
    </TableRow>
  );
}

// ─── MonthlyPresence ──────────────────────────────────────────────────────────
// ─── cell colour constants ────────────────────────────────────────────────────
const SICK_TYPE_IDS = new Set([2, 10]); // Pushim Mjekësor / Lëndim-sëmundje pune
const PCELL = {
  present:  { bg:'#F0FDF4', border:'#86EFAC', dot:'#16A34A', tip:'Prezent (i regjistruar)' },
  leave:    { bg:'#EFF6FF', border:'#93C5FD', dot:'#2563EB', tip:'Pushim vjetor i aprovuar' },
  sick:     { bg:'#FFF7ED', border:'#FDBA74', dot:'#EA580C', tip:'Mungesë shëndetësore / mjekësore' },
  pending:  { bg:'#FEFCE8', border:'#FDE047', dot:'#CA8A04', tip:'Kërkesë pushimi në pritje' },
  absent:   { bg:'#FFF1F2', border:'#FECDD3', dot:'#E11D48', tip:'Mungon (pa regjistrim)' },
  holiday:  { bg:'#FEF2F2', border:'#FECACA', dot:'#DC2626', tip:'Festë zyrtare' },
  weekend:  { bg:'#F8FAFC', border:'transparent', dot:'#CBD5E1', tip:'Fund-javë' },
  future:   { bg:'#F9FAFB', border:'transparent', dot:'#D1D5DB', tip:'Ditë e ardhshme' },
};

function MonthlyPresenceGrid({ year, month, workers, leaves, holidays, presenceEvents, search, departments, groupByDept }) {
  const dim       = daysInMonth(year, month);
  const monthDays = Array.from({ length: dim }, (_, i) => i + 1);
  const todayStr  = new Date().toISOString().split('T')[0];

  // index events by worker+day for O(1) lookup
  const eventIndex = React.useMemo(() => {
    const map = {};
    (presenceEvents || []).forEach(ev => {
      const wid = String(ev.WorkerId ?? ev.workerId ?? '');
      const day = (ev.At || ev.at || '').split('T')[0];
      if (!wid || !day) return;
      if (!map[wid]) map[wid] = {};
      if (!map[wid][day]) map[wid][day] = [];
      map[wid][day].push(ev);
    });
    return map;
  }, [presenceEvents]);

  const isHolidayDay = (d) => holidays.some(h => h.Date === isoDate(year, month, d));

  const getLeave = (worker, day) => {
    const ds  = isoDate(year, month, day);
    const wid = String(worker.Id ?? worker.id);
    const wLeaves = leaves.filter(l =>
      String(l.WorkerId ?? l.workerId) === wid &&
      ds >= l.StartDate && ds <= l.EndDate && l.Status !== 'rejected'
    );
    if (!wLeaves.length) return null;
    return wLeaves.find(l => l.Status === 'approved') || wLeaves[0];
  };

  const getEvents = (worker, day) => {
    const wid = String(worker.Id ?? worker.id);
    const ds  = isoDate(year, month, day);
    return eventIndex[wid]?.[ds] || [];
  };

  const getCellType = (worker, day) => {
    const ds      = isoDate(year, month, day);
    const weekend = isWeekend(year, month, day);
    const hday    = isHolidayDay(day);
    if (hday)    return 'holiday';
    if (weekend) return 'weekend';
    const leave  = getLeave(worker, day);
    if (leave) {
      if (leave.Status === 'approved') {
        const tid = leave.LeaveTypeId ?? leave.leaveTypeId ?? leave.HolidayType;
        return SICK_TYPE_IDS.has(Number(tid)) ? 'sick' : 'leave';
      }
      return 'pending';
    }
    if (ds > todayStr) return 'future';
    const evts = getEvents(worker, day);
    return evts.length > 0 ? 'present' : 'absent';
  };

  // filter workers by search prop
  // Rreshtat e kalendarit janë roster i sotëm. Lista e plotë `workers` mbetet
  // ashtu siç është më lart — prej saj merren emrat e kërkesave të vjetra.
  const filteredWorkers = React.useMemo(() => {
    const roster = activeWorkers(workers);
    const q = (search || '').toLowerCase();
    if (!q) return roster;
    return roster.filter(w => {
      const name = `${w.FirstName||w.Name||''} ${w.LastName||w.Surname||''}`.toLowerCase();
      return name.includes(q);
    });
  }, [workers, search]);

  // flat rows list (worker rows + optional dept-header rows interspersed)
  const flatRows = React.useMemo(() => {
    const getDeptName = (w) => {
      const deptId = w.Department ?? w.DepartmentId;
      if (!deptId) return 'Pa departament';
      const dept = (departments || []).find(d => String(d.Id ?? d.id) === String(deptId));
      return dept ? (dept.Name || dept.name || String(deptId)) : String(deptId);
    };
    if (!groupByDept) return filteredWorkers.map(w => ({ type: 'worker', worker: w }));
    const map = new Map();
    filteredWorkers.forEach(w => {
      const key = String(w.Department ?? w.DepartmentId ?? '');
      const name = getDeptName(w);
      if (!map.has(key)) map.set(key, { name, workers: [] });
      map.get(key).workers.push(w);
    });
    const groups = Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    const rows = [];
    groups.forEach(g => {
      rows.push({ type: 'header', name: g.name, count: g.workers.length });
      g.workers.forEach(w => rows.push({ type: 'worker', worker: w }));
    });
    return rows;
  }, [filteredWorkers, groupByDept, departments]);

  // per-day totals (for summary row)
  const dayTotals = React.useMemo(() => {
    return monthDays.map(d => {
      const hday = isHolidayDay(d);
      const wend = isWeekend(year, month, d);
      if (hday || wend) return null;
      let present = 0, absent = 0, onLeave = 0, sick = 0;
      filteredWorkers.forEach(w => {
        const t = getCellType(w, d);
        if (t === 'present') present++;
        else if (t === 'absent') absent++;
        else if (t === 'leave' || t === 'pending') onLeave++;
        else if (t === 'sick') sick++;
      });
      return { present, absent, onLeave, sick };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredWorkers, year, month, leaves, presenceEvents, holidays]);

  const CELL_W = 28;
  const NAME_W = 160;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ overflowX: 'auto', overflowY: 'auto' }}>
      <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', borderCollapse: 'separate' }}>
        <TableHead>
          {/* ── Day-number row ── */}
          <TableRow>
            <TableCell sx={{
              width: NAME_W, minWidth: NAME_W, fontWeight: 700, fontSize: '0.72rem',
              position: 'sticky', left: 0, zIndex: 4, bgcolor: '#F8FAFC',
              borderRight: '2px solid #E2E8F0', py: 0.75, pl: 1.5,
            }}>
              <Stack direction="row" alignItems="center" gap={0.5}>
                <PersonIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                <span>Punëtori</span>
                {filteredWorkers.length !== workers.length && (
                  <Chip size="small" label={filteredWorkers.length} color="primary" sx={{ height: 14, fontSize: 9 }} />
                )}
              </Stack>
            </TableCell>
            {/* Day columns — hidden on mobile */}
            {!isMobile && monthDays.map((d, i) => {
              const weekend = isWeekend(year, month, d);
              const hday    = isHolidayDay(d);
              const tot     = dayTotals[i];
              return (
                <TableCell key={d} align="center" sx={{
                  width: CELL_W, minWidth: CELL_W, maxWidth: CELL_W,
                  px: 0, py: 0.5,
                  bgcolor: hday ? '#FEF2F2' : weekend ? '#F8FAFC' : '#F8FAFC',
                  borderBottom: '1px solid #E2E8F0',
                }}>
                  <Box sx={{ fontWeight: 800, fontSize: '0.65rem', lineHeight: 1, color: hday ? '#DC2626' : weekend ? '#94A3B8' : '#334155' }}>{d}</Box>
                  <Box sx={{ fontSize: '0.5rem', color: hday ? '#F87171' : '#94A3B8', lineHeight: 1.2, textTransform: 'uppercase' }}>
                    {new Date(year, month, d).toLocaleString('sq-AL', { weekday: 'short' }).slice(0, 2)}
                  </Box>
                  {tot && (
                    <Box sx={{ mt: 0.25 }}>
                      <Box sx={{ height: 2, borderRadius: 1, bgcolor: '#86EFAC', mb: '1px',
                        width: `${filteredWorkers.length > 0 ? Math.min(100, (tot.present / filteredWorkers.length) * 100) : 0}%`, mx: 'auto' }} />
                    </Box>
                  )}
                </TableCell>
              );
            })}
            {/* Summary columns */}
            {[
              { lbl: 'P',  ttip: 'Ditë prezent',             clr: '#16A34A', Icon: CheckCircleIcon   },
              { lbl: 'L',  ttip: 'Ditë pushim vjetor',     clr: '#2563EB', Icon: BeachAccessIcon   },
              { lbl: 'Sj', ttip: 'Ditë mjekësore',         clr: '#EA580C', Icon: LocalHospitalIcon },
              { lbl: 'M',  ttip: 'Ditë mungesë',           clr: '#E11D48', Icon: CancelIcon        },
              { lbl: 'F',  ttip: 'Ditë festë',             clr: '#DC2626', Icon: EventBusyIcon     },
              { lbl: 'Tj', ttip: 'Ditë pune jo-prezent',   clr: '#64748B', Icon: WorkHistoryIcon   },
            ].map(({ lbl, ttip, clr, Icon }) => (
              <TableCell key={lbl} align="center" sx={{
                minWidth: 46, py: 0.75,
                borderLeft: lbl==='P' ? '2px solid #E2E8F0' : undefined,
                bgcolor: '#F8FAFC',
              }}>
                <Tooltip title={ttip} placement="top">
                  <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.4}>
                    <Icon sx={{ fontSize: 13, color: clr, flexShrink: 0 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', whiteSpace: 'nowrap' }}>{lbl}</Typography>
                  </Stack>
                </Tooltip>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {flatRows.map((row, ri) => {
            if (row.type === 'header') {
              return (
                <TableRow key={`dept-${row.name}`} sx={{ bgcolor: '#EFF6FF' }}>
                  <TableCell
                    colSpan={(!isMobile ? monthDays.length : 0) + 7}
                    sx={{
                      position: 'sticky', left: 0, zIndex: 3,
                      pl: 1.5, py: 0.5,
                      bgcolor: '#EFF6FF',
                      borderTop: '2px solid #BFDBFE',
                      borderBottom: '1px solid #BFDBFE',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <GroupsIcon sx={{ fontSize: 14, color: '#2563EB' }} />
                      <Typography variant="caption" fontWeight={800} color="#1D4ED8" sx={{ fontSize: '0.72rem' }}>
                        {row.name}
                      </Typography>
                      <Chip size="small" label={row.count} sx={{ height: 16, fontSize: '0.62rem', bgcolor: '#DBEAFE', color: '#1D4ED8', fontWeight: 700 }} />
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            }
            const worker = row.worker;
            const wi = ri;
            let pCount = 0, lCount = 0, aCount = 0, sCount = 0, hCount = 0;
            const cells = monthDays.map(d => {
              const type = getCellType(worker, d);
              const isWorkDay = !isWeekend(year, month, d) && !isHolidayDay(d);
              const isFuture = isoDate(year, month, d) > todayStr;
              if (isWorkDay && !isFuture) {
                if (type === 'present') pCount++;
                else if (type === 'leave' || type === 'pending') lCount++;
                else if (type === 'absent') aCount++;
                else if (type === 'sick') sCount++;
              }
              if (type === 'holiday') hCount++;
              const c = PCELL[type];
              const evts = getEvents(worker, d);
              const leave = getLeave(worker, d);
              const tipLines = [
                c.tip,
                evts.length > 0 ? `Ngjarje: ${evts.length}` : null,
                leave ? `Pushim: ${leave.LeaveTypeName || leave.leaveType || leave.Type || ''}` : null,
              ].filter(Boolean).join(' · ');
              return { d, type, c, tipLines };
            });

            const totalWork = pCount + lCount + aCount + sCount;
            const pctPresent = totalWork > 0 ? Math.round((pCount / totalWork) * 100) : 0;

            return (
              <TableRow key={worker.Id}
                sx={{ '&:hover': { bgcolor: '#F8FAFC' }, bgcolor: wi % 2 === 0 ? '#FFFFFF' : '#FAFAFA' }}>
                {/* Worker name cell */}
                <TableCell sx={{
                  position: 'sticky', left: 0, zIndex: 2,
                  borderRight: '2px solid #E2E8F0', py: 0.5, pl: 1.5,
                  bgcolor: wi % 2 === 0 ? '#FFFFFF' : '#FAFAFA',
                  whiteSpace: 'nowrap', maxWidth: NAME_W, overflow: 'hidden',
                }}>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Avatar sx={{ width: 22, height: 22, fontSize: '0.6rem',
                      bgcolor: `hsl(${((worker.Id || 1) * 67) % 360}, 55%, 50%)`,
                      flexShrink: 0 }}>
                      {workerInitials(`${worker.FirstName||worker.Name||''} ${worker.LastName||worker.Surname||''}`)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" fontWeight={700}
                        sx={{ fontSize: '0.73rem', lineHeight: 1.2, display: 'block',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {worker.FirstName||worker.Name} {worker.LastName||worker.Surname}
                      </Typography>
                      <Stack direction="row" gap={0.4} alignItems="center">
                        {totalWork > 0 && (
                          <Box sx={{ width: 36, height: 3, borderRadius: 1, bgcolor: '#E2E8F0', overflow: 'hidden' }}>
                            <Box sx={{ height: '100%', width: `${pctPresent}%`, bgcolor: pctPresent >= 80 ? '#16A34A' : pctPresent >= 50 ? '#CA8A04' : '#E11D48', borderRadius: 1 }} />
                          </Box>
                        )}
                        <Typography sx={{ fontSize: '0.58rem', color: 'text.disabled' }}>{pctPresent}%</Typography>
                      </Stack>
                    </Box>
                  </Stack>
                </TableCell>

                {/* Day cells — hidden on mobile */}
                {!isMobile && cells.map(({ d, type, c, tipLines }) => {
                  const CellIcon =
                    type === 'present'  ? CheckCircleIcon   :
                    type === 'leave'    ? BeachAccessIcon   :
                    type === 'sick'     ? LocalHospitalIcon :
                    type === 'pending'  ? HourglassTopIcon  :
                    type === 'absent'   ? CancelIcon        :
                    type === 'holiday'  ? EventBusyIcon     :
                    null;
                  return (
                    <Tooltip key={d} title={tipLines} placement="top" disableInteractive>
                      <TableCell align="center" sx={{
                        px: 0, py: 0.25, bgcolor: c.bg,
                        border: `1px solid ${c.border}`,
                        transition: 'opacity .12s',
                        '&:hover': { opacity: 0.7 },
                      }}>
                        {CellIcon
                          ? <CellIcon sx={{ fontSize: 13, color: c.dot, display: 'block', mx: 'auto' }} />
                          : <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c.dot, mx: 'auto' }} />}
                      </TableCell>
                    </Tooltip>
                  );
                })}
                {/* End day cells */}

                {/* Summary */}
                <TableCell align="center" sx={{ fontWeight: 700, color: '#16A34A', fontSize: '0.72rem', borderLeft: '2px solid #E2E8F0', py: 0.5 }}>{pCount}</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#2563EB', fontSize: '0.72rem', py: 0.5 }}>{lCount}</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#EA580C', fontSize: '0.72rem', py: 0.5 }}>{sCount}</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#E11D48', fontSize: '0.72rem', py: 0.5 }}>{aCount}</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#DC2626', fontSize: '0.72rem', py: 0.5 }}>{hCount}</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#64748B', fontSize: '0.72rem', py: 0.5 }}>{totalWork - pCount}</TableCell>
              </TableRow>
            );
          })}

          {/* ── Summary row ── */}
          {filteredWorkers.length > 0 && (
            <TableRow sx={{ bgcolor: '#F0F9FF', borderTop: '2px solid #E2E8F0' }}>
              <TableCell sx={{ position: 'sticky', left: 0, bgcolor: '#F0F9FF', borderRight: '2px solid #E2E8F0', pl: 1.5, py: 0.5 }}>
                <Typography variant="caption" fontWeight={800} color="primary.main" sx={{ fontSize: '0.7rem' }}>Totali / ditë</Typography>
              </TableCell>
              {dayTotals.map((tot, i) => {
                const d = i + 1;
                const hday = isHolidayDay(d);
                const wend = isWeekend(year, month, d);
                if (hday || wend || !tot) return (
                  <TableCell key={d} align="center" sx={{ bgcolor: hday?'#FEF2F2':'#F8FAFC', px:0, py:0.25 }} />
                );
                const pct = filteredWorkers.length > 0 ? (tot.present / filteredWorkers.length) * 100 : 0;
                return (
                  <TableCell key={d} align="center" sx={{ px: 0, py: 0.4, bgcolor: '#F0F9FF' }}>
                    <Tooltip title={`Prezent: ${tot.present} · Pushim: ${tot.onLeave} · Shëndetësor: ${tot.sick||0} · Mungesë: ${tot.absent}`} placement="top" disableInteractive>
                      <Box>
                        <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: '#0369A1', lineHeight: 1 }}>{tot.present}</Typography>
                        <Box sx={{ width: '80%', height: 2, borderRadius: 1, bgcolor: '#BAE6FD', mx: 'auto', mt: '1px', overflow: 'hidden' }}>
                          <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: '#0284C7' }} />
                        </Box>
                      </Box>
                    </Tooltip>
                  </TableCell>
                );
              })}
              <TableCell colSpan={4} />
            </TableRow>
          )}

          {filteredWorkers.length === 0 && (
            <TableRow>
              <TableCell colSpan={dim + 4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                {workers.length === 0 ? 'Nuk ka të dhëna.' : 'Nuk u gjet asnjë punëtor.'}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}

// ─── BigCalendarView ─────────────────────────────────────────────────────────
function BigCalendarView({ year, month, leaves, leaveTypes, workers, onDayDoubleClick }) {
  const [calStatus, setCalStatus] = React.useState('all');
  const [calWorker, setCalWorker] = React.useState('all');
  const [calType,   setCalType]   = React.useState('all');

  // Normalize any date string to YYYY-MM-DD (strips time component if present)
  const norm = s => s ? String(s).substring(0, 10) : '';
  const fmtD = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  const todayStr = fmtD(new Date());

  const COL_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Build all weeks that overlap with this month (Sun–Sat)
  const weeks = React.useMemo(() => {
    const ws = [];
    const first = new Date(year, month, 1);
    const start = new Date(first);
    start.setDate(start.getDate() - start.getDay()); // back to Sunday
    const cur = new Date(start);
    for (let w = 0; w < 6; w++) {
      const week = [];
      for (let i = 0; i < 7; i++) { week.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
      if (week.some(d => d.getMonth() === month)) ws.push(week);
    }
    return ws;
  }, [year, month]);

  const STATUS_BAR = {
    approved: '#20c9b3',
    pending:  '#f59e0b',
    rejected: '#94a3b8',
  };

  const visibleLeaves = React.useMemo(() => {
    let list = leaves.filter(l => norm(l.StartDate) && norm(l.EndDate));
    if (calStatus !== 'all') list = list.filter(l => l.Status === calStatus);
    if (calWorker !== 'all') list = list.filter(l => String(l.WorkerId ?? l.workerId) === calWorker);
    if (calType   !== 'all') list = list.filter(l => String(l.LeaveTypeId ?? l.leaveTypeId) === calType);
    return list;
  }, [leaves, calStatus, calWorker, calType]);

  // For a given week, return all overlapping events with their track/slot assignment
  const getWeekEvents = (week) => {
    const wStart = fmtD(week[0]);
    const wEnd   = fmtD(week[6]);

    const hits = visibleLeaves.filter(l => norm(l.StartDate) <= wEnd && norm(l.EndDate) >= wStart);
    // Sort so earlier-starting events get lower tracks
    const sorted = [...hits].sort((a, b) => norm(a.StartDate).localeCompare(norm(b.StartDate)));

    const trackEndCol = []; // last column occupied per track
    return sorted.map(leave => {
      const ls = norm(leave.StartDate);
      const le = norm(leave.EndDate);
      const continPrev = ls < wStart;
      const continNext = le > wEnd;
      const effStart = continPrev ? wStart : ls;
      const effEnd   = continNext ? wEnd   : le;

      // Find column index in this week
      const sIdx = week.findIndex(d => fmtD(d) === effStart);
      const eIdx = week.findIndex(d => fmtD(d) === effEnd);
      const sCol = sIdx === -1 ? 0 : sIdx;
      const eCol = eIdx === -1 ? 6 : eIdx;

      // Greedy first-fit track assignment
      let track = trackEndCol.findIndex(te => te < sCol);
      if (track === -1) track = trackEndCol.length;
      trackEndCol[track] = eCol;

      return { leave, sCol, eCol, track, continPrev, continNext };
    });
  };

  const HDR_H   = 38; // px: day-number row height
  const TRACK_H = 24; // px: height per event track
  const PAD_B   = 6;  // px: bottom padding per row

  const getTypeIcon = (leaveTypeId) => {
    const n = (leaveTypes.find(t => String(t.Id) === String(leaveTypeId))?.Name || '').toLowerCase();
    const sx = { fontSize: 10, color: '#fff' };
    if (n.includes('vjetor') || n.includes('annual'))                              return <BeachAccessIcon sx={sx} />;
    if (n.includes('mjek') || n.includes('shëndet') || n.includes('sick'))         return <LocalHospitalIcon sx={sx} />;
    if (n.includes('gjak') || n.includes('blood'))                                 return <LocalHospitalIcon sx={sx} />;
    if (n.includes('martesor') || n.includes('wedding'))                           return <FavoriteIcon sx={sx} />;
    if (n.includes('pa pagesë') || n.includes('unpaid') || n.includes('pa pages')) return <BlockIcon sx={sx} />;
    if (n.includes('me pagesë') || n.includes('paid') || n.includes('me pages'))   return <WorkHistoryIcon sx={sx} />;
    if (n.includes('jashtë') || n.includes('special') || n.includes('extraordinary')) return <InfoOutlinedIcon sx={sx} />;
    return <BeachAccessIcon sx={sx} />;
  };

  return (
    <Box sx={{ userSelect: 'none' }}>

      {/* ── Filter bar ── */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} flexWrap="wrap"
        sx={{ px: 2, py: 1.25, borderBottom: '1px solid #e5e7eb', bgcolor: '#f8fafc' }}>
        <Stack direction="row" spacing={0.5} flexShrink={0}>
          {[
            { v: 'all',      label: 'Të gjitha' },
            { v: 'approved', label: 'Aprovuar'  },
            { v: 'pending',  label: 'Pritje'    },
            { v: 'rejected', label: 'Refuzuar'  },
          ].map(s => (
            <Chip key={s.v} label={s.label} size="small" clickable
              variant={calStatus === s.v ? 'filled' : 'outlined'}
              color={calStatus === s.v ? (s.v==='approved'?'success':s.v==='pending'?'warning':s.v==='all'?'primary':'default') : 'default'}
              onClick={() => setCalStatus(s.v)}
              sx={{ fontSize: '0.73rem', height: 26, fontWeight: calStatus===s.v ? 700 : 400 }} />
          ))}
        </Stack>
        <TextField select size="small" value={calWorker} onChange={e => setCalWorker(e.target.value)}
          sx={{ minWidth: 200, '& .MuiInputBase-root': { borderRadius: 2, fontSize: '0.82rem', height: 32 } }}>
          <MenuItem value="all"><em>Të gjithë punëtorët</em></MenuItem>
          {workers.map(w => (
            <MenuItem key={w.Id??w.id} value={String(w.Id??w.id)} sx={{ fontSize: '0.82rem' }}>
              {w.FirstName||w.Name} {w.LastName||w.Surname}
            </MenuItem>
          ))}
        </TextField>
        <TextField select size="small" value={calType} onChange={e => setCalType(e.target.value)}
          sx={{ minWidth: 170, '& .MuiInputBase-root': { borderRadius: 2, fontSize: '0.82rem', height: 32 } }}>
          <MenuItem value="all"><em>Të gjitha llojet</em></MenuItem>
          {leaveTypes.map(t => (
            <MenuItem key={t.Id} value={String(t.Id)} sx={{ fontSize: '0.82rem' }}>{t.Name}</MenuItem>
          ))}
        </TextField>
        <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto', flexShrink: 0 }}>
          {visibleLeaves.length} kërkesa
        </Typography>
      </Stack>

      {/* ── Day-name header ── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', bgcolor: '#fff', borderBottom: '1px solid #e5e7eb' }}>
        {COL_HEADERS.map((h, i) => (
          <Box key={h} sx={{
            py: 1, textAlign: 'center', fontWeight: 600, fontSize: '0.78rem',
            color: (i===0||i===6) ? '#94a3b8' : '#6b7280',
            borderRight: i < 6 ? '1px solid #e5e7eb' : 'none',
            textTransform: 'uppercase', letterSpacing: 0.5,
          }}>
            {h}
          </Box>
        ))}
      </Box>

      {/* ── Week rows ── */}
      {weeks.map((week, wi) => {
        const evts    = getWeekEvents(week);
        const nTracks = evts.length > 0 ? Math.max(...evts.map(e => e.track)) + 1 : 0;

        return (
          <Box key={wi} sx={{ borderBottom: wi < weeks.length-1 ? '1px solid #e5e7eb' : 'none' }}>

            {/* ── Day numbers row ── */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', height: HDR_H }}>
              {week.map((day, di) => {
                const inMonth = day.getMonth() === month;
                const isToday = fmtD(day) === todayStr;
                const isWend  = di === 0 || di === 6;
                return (
                  <Box key={di} sx={{
                    height: '100%',
                    borderRight: di < 6 ? '1px solid #e5e7eb' : 'none',
                    bgcolor: !inMonth ? '#f9fafb' : isToday ? '#eff6ff' : '#fff',
                    display: 'flex', justifyContent: 'flex-end', pt: 0.75, pr: 1,
                    cursor: inMonth ? 'pointer' : 'default',
                    transition: 'background .12s',
                    '&:hover': inMonth ? { bgcolor: isToday ? '#dbeafe' : '#e8f4f8' } : {},
                  }}
                  title={inMonth ? 'Dyfishëklik për të shtuar pushim' : undefined}
                  onDoubleClick={() => inMonth && onDayDoubleClick && onDayDoubleClick(fmtD(day))}>
                    <Box sx={{
                      width: 26, height: 26, borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: isToday ? '#3b82f6' : 'transparent',
                      color: isToday ? '#fff' : !inMonth ? '#cbd5e1' : isWend ? '#9ca3af' : '#1e293b',
                      fontWeight: isToday ? 800 : 400,
                      fontSize: '0.82rem',
                    }}>
                      {day.getDate()}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* ── Events rows ── */}
            {nTracks > 0 && (
              <Box sx={{ position: 'relative', height: nTracks * TRACK_H + PAD_B }}>
                {/* Column background / border lines */}
                <Box sx={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', pointerEvents: 'none' }}>
                  {week.map((day, di) => (
                    <Box key={di} sx={{
                      height: '100%',
                      borderRight: di < 6 ? '1px solid #e5e7eb' : 'none',
                      bgcolor: day.getMonth() !== month ? '#f9fafb' : '#fff',
                    }} />
                  ))}
                </Box>

                {/* Event bars */}
                {evts.map(({ leave, sCol, eCol, track, continPrev, continNext }, ei) => {
                  const bg       = STATUS_BAR[leave.Status] || STATUS_BAR.pending;
                  const typeName = leaveTypes.find(t => String(t.Id) === String(leave.LeaveTypeId))?.Name || 'Pushim';
                  const statLbl  = leave.Status === 'approved' ? 'Approved' : leave.Status === 'pending' ? 'Pritje' : 'Refuzuar';
                  const label    = `${leave.WorkerName || ''} - ${typeName} - ${statLbl}`;
                  const colW     = 100 / 7;
                  // Edge-to-edge bars: no gap at week boundaries, small radius at actual start/end
                  const left  = continPrev ? `${sCol * colW}%`         : `calc(${sCol * colW}% + 4px)`;
                  const right = continNext ? `${(6 - eCol) * colW}%`   : `calc(${(6 - eCol) * colW}% + 4px)`;
                  const blr   = continPrev ? 0 : 5;
                  const brr   = continNext ? 0 : 5;

                  return (
                    <Tooltip key={leave.Id ?? ei}
                      title={`${leave.WorkerName||''}  ·  ${typeName}  ·  ${norm(leave.StartDate)} → ${norm(leave.EndDate)}`}
                      placement="top" disableInteractive>
                      <Box sx={{
                        position: 'absolute',
                        top: track * TRACK_H + 2,
                        left, right,
                        height: TRACK_H - 5,
                        bgcolor: bg,
                        borderRadius: `${blr}px ${brr}px ${brr}px ${blr}px`,
                        display: 'flex', alignItems: 'center', gap: '5px',
                        px: '8px',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        zIndex: 1,
                        '&:hover': { filter: 'brightness(0.9)' },
                        transition: 'filter .1s',
                      }}>
                        {continPrev && <Typography sx={{ color:'#fff', fontSize:'0.7rem', fontWeight:800, lineHeight:1, mr:'-2px' }}>‹</Typography>}
                        <Box sx={{ width:14, height:14, borderRadius:'50%', bgcolor:'rgba(0,0,0,0.18)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                          {getTypeIcon(leave.LeaveTypeId)}
                        </Box>
                        <Typography sx={{ fontSize:'0.72rem', fontWeight:600, color:'#fff', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', lineHeight:1 }}>
                          {label}
                        </Typography>
                        {continNext && <Typography sx={{ color:'#fff', fontSize:'0.7rem', fontWeight:800, lineHeight:1, ml:'auto', flexShrink:0 }}>›</Typography>}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            )}

            {/* Empty row — keep minimum height when no events */}
            {nTracks === 0 && <Box sx={{ height: PAD_B + 4,
              display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
              {week.map((day, di) => (
                <Box key={di} sx={{
                  height: '100%',
                  borderRight: di < 6 ? '1px solid #e5e7eb' : 'none',
                  bgcolor: day.getMonth() !== month ? '#f9fafb' : '#fff',
                }} />
              ))}
            </Box>}
          </Box>
        );
      })}
    </Box>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function HolidayCalendarPage() {
  const now = new Date();
  const [year,  setYear]  = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [tab,   setTab]   = useState(0);  // 0=Zyrtare 1=Bllokuara 2=Kërkesat
  const [mainTab, setMainTab] = useState(0); // 0=Kalendari 1=Prezenca Mujore

  const [holidays,       setHolidays]       = useState([]);
  const [blackouts,      setBlackouts]      = useState([]);
  const [leaves,         setLeaves]         = useState([]);
  const [workers,        setWorkers]        = useState([]);
  const [departments,    setDepartments]    = useState([]);
  const [leaveTypes,     setLeaveTypes]     = useState([]);
  const [presenceEvents, setPresenceEvents] = useState([]);
  const [presenceSearch, setPresenceSearch] = useState('');
  const [presenceLoading,setPresenceLoading]= useState(false);
  const [groupByDept,    setGroupByDept]    = useState(false);

  const [dayDetail,     setDayDetail]    = useState(null);
  const [approveReq,    setApproveReq]   = useState(null);  // approve dialog
  const [rejectReq,     setRejectReq]    = useState(null);  // reject dialog
  const [rejectNote,    setRejectNote]   = useState('');
  const [detailReq,     setDetailReq]    = useState(null);  // detail dialog
  const [saving,        setSaving]       = useState(false);
  const [filterStatus,  setFilterStatus] = useState('all');

  const [editHoliday,   setEditHoliday]  = useState(null);
  const [deleteHoliday, setDeleteHoliday]= useState(null);
  const [editBlackout,  setEditBlackout] = useState(null);
  // Politikat e pushimit sipas projektit: lista e projekteve dhe fushëveprimi
  // i secilës politikë (blackout / festë) — zbatimin e bën serveri.
  const [projects,      setProjects]     = useState([]);
  const [policyProjects,setPolicyProjects]=useState({ blackout: {}, holiday: {} });
  const [deleteBlackout,setDeleteBlackout]=useState(null);

  const [snack, setSnack] = useState('');
  const [newLeaveOpen,    setNewLeaveOpen]    = useState(false);
  const [newLeaveForm,    setNewLeaveForm]    = useState({ WorkerId: '', LeaveTypeId: '', StartDate: '', EndDate: '', Reason: '' });
  const [newLeaveSaving,  setNewLeaveSaving]  = useState(false);

  // ─── load ──────────────────────────────────────────────────────────────────
  const load = useCallback(() => {
    apiService().get('publicHolidays').then(r => { if (r.success) setHolidays(r.data); });
    apiService().get('leaveBlackouts').then(r => { if (r.success) setBlackouts(r.data); });
    apiService().get('leaveTypes').then(r => { if (r.success) setLeaveTypes(r.data); });
    apiService().get('departments').then(r => { if (r.success) setDepartments(r.data || []); });
    apiService().get('projects').then(r => { if (r?.success) setProjects((r.data || []).map(p => ({ id: p.Id ?? p.id, name: p.Name || p.name || `#${p.Id ?? p.id}` }))); }).catch(() => {});
    Promise.all(['blackout', 'holiday'].map(k => authFetch(`/api/leave-policies/projects?kind=${k}`).then(r => r.json()).catch(() => null)))
      .then(([b, h]) => setPolicyProjects({ blackout: b?.data || {}, holiday: h?.data || {} }));
    Promise.all([
      apiService().get('workers'),
      apiService().get('leaveRequests'),
    ]).then(([wRes, lRes]) => {
      const ws = wRes.success ? wRes.data : [];
      setWorkers(ws);
      if (lRes.success) {
          const enriched = (lRes.data || []).map(l => {
          const w = ws.find(w => (w.Id ?? w.id) === (l.WorkerId ?? l.workerId));
          const firstName = w?.FirstName || w?.Name || '';
          const lastName  = w?.LastName  || w?.Surname || '';
          return { ...l, WorkerName: w ? `${firstName} ${lastName}`.trim() : l.FullName || `Punëtor #${l.WorkerId}` };
        });
        setLeaves(enriched);
      }
    });
  }, []);

  // load presence events whenever month/year changes
  const loadPresence = useCallback(async () => {
    setPresenceLoading(true);
    try {
      const from = isoDate(year, month, 1);
      const to   = isoDate(year, month, daysInMonth(year, month));
      const evts = await presenceService().getRange(from, to);
      setPresenceEvents(evts || []);
    } catch (_) {
      setPresenceEvents([]);
    } finally {
      setPresenceLoading(false);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadPresence(); }, [loadPresence]);

  // ─── nav ───────────────────────────────────────────────────────────────────
  const prevMonth = () => { if (month===0){setMonth(11);setYear(y=>y-1);}else setMonth(m=>m-1); };
  const nextMonth = () => { if (month===11){setMonth(0);setYear(y=>y+1);}else setMonth(m=>m+1); };

  // ─── derived ───────────────────────────────────────────────────────────────
  const monthHolidays = useMemo(() => holidays.filter(h => {
    const [hy,hm] = h.Date.split('-').map(Number);
    return hy===year && hm===month+1;
  }), [holidays, year, month]);

  const monthBlackouts = useMemo(() => {
    const from = isoDate(year,month,1), to = isoDate(year,month,daysInMonth(year,month));
    return blackouts.filter(b => b.DateFrom<=to && b.DateTo>=from);
  }, [blackouts, year, month]);

  const monthLeaves = useMemo(() => {
    const from = isoDate(year,month,1), to = isoDate(year,month,daysInMonth(year,month));
    return leaves.filter(l => l.StartDate<=to && l.EndDate>=from && l.Status!=='rejected');
  }, [leaves, year, month]);

  const pendingLeaves = useMemo(() => leaves.filter(l=>l.Status==='pending'), [leaves]);

  const filteredLeaves = useMemo(() =>
    filterStatus==='all' ? leaves : leaves.filter(l=>l.Status===filterStatus)
  , [leaves, filterStatus]);

  // ─── approve / reject ──────────────────────────────────────────────────────
  const doApprove = async () => {
    if (!approveReq) return;
    setSaving(true);
    const currentUser = getUserDisplayName('Admin');
    const updated = { ...approveReq, Status:'approved', ReviewedBy:currentUser, ReviewedAt:today, ReviewNotes:'' };
    const r = await apiService().put('leaveRequests', updated);
    setSaving(false);
    if (r?.success) { setSnack(`Kërkesa e ${approveReq.WorkerName} u aprovua!`); setApproveReq(null); load(); }
    else setSnack('Gabim gjatë aprovimit.');
  };

  const doReject = async () => {
    if (!rejectReq) return;
    setSaving(true);
    const currentUser = getUserDisplayName('Admin');
    const updated = { ...rejectReq, Status:'rejected', ReviewedBy:currentUser, ReviewedAt:today, ReviewNotes:rejectNote };
    const r = await apiService().put('leaveRequests', updated);
    setSaving(false);
    if (r?.success) { setSnack(`Kërkesa e ${rejectReq.WorkerName} u refuzua.`); setRejectReq(null); setRejectNote(''); load(); }
    else setSnack('Gabim gjatë refuzimit.');
  };

  // ─── holiday CRUD ──────────────────────────────────────────────────────────
  const BLANK_H = { Id:0, Date:'', Name:'', NameEn:'', Year:year, Type:'fixed', IsNational:true, WorkingDay:false, AppliesToAllProjects:true, ProjectIds:[] };
  const saveHoliday = async () => {
    if (!editHoliday?.Date || !editHoliday?.Name?.trim()) { setSnack('Data dhe emri janë të detyrueshëm!'); return; }
    const scopeErr = validateScope(editHoliday);
    if (scopeErr) { setSnack(scopeErr); return; }
    const payload = { ...editHoliday, Year: parseInt(editHoliday.Date.split('-')[0]) };
    delete payload.ProjectIds;
    let id = editHoliday.Id;
    if (editHoliday.Id===0) { const r = await apiService().post('publicHolidays', { ...payload, Id:undefined }); id = r?.data?.Id ?? r?.data?.id; }
    else                    await apiService().put('publicHolidays', payload);
    const se = await saveScope('holiday', id, editHoliday);
    setEditHoliday(null); load(); setSnack(se || 'Pushimi zyrtar u ruajt.');
  };
  const deleteHolidayFn = async () => {
    await apiService().delete('publicHolidays', deleteHoliday);
    setDeleteHoliday(null); load(); setSnack('Pushimi u fshi.');
  };

  // ─── blackout CRUD ─────────────────────────────────────────────────────────
  const BLANK_B = { Id:0, Name:'', DateFrom:'', DateTo:'', Reason:'', Type:'other', Strict:false, AppliesAll:true, Departments:[], AppliesToAllProjects:true, ProjectIds:[] };
  // Fushëveprimi i projekteve: «të gjitha» ose lista. Ruhet me PUT
  // /api/leave-policies/:kind/:id/projects PAS rreshtit, që politika të ketë id.
  const scopeOf = (kind, item) => ({
    all: item?.AppliesToAllProjects !== false,
    ids: item?.ProjectIds ?? (policyProjects[kind]?.[item?.Id] || []),
  });
  const validateScope = (item) => (item?.AppliesToAllProjects === false && !(item?.ProjectIds?.length) ? 'Zgjidhni të paktën një projekt, ose «Të gjitha projektet».' : '');
  const saveScope = async (kind, id, item) => {
    if (!id) return '';
    try {
      const r = await authFetch(`/api/leave-policies/${kind}/${id}/projects`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appliesToAllProjects: item.AppliesToAllProjects !== false, projectIds: item.AppliesToAllProjects === false ? (item.ProjectIds || []) : [] }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j?.success) return `U ruajt, por fushëveprimi i projekteve dështoi: ${j?.error || r.status}`;
    } catch (e) { return `U ruajt, por fushëveprimi i projekteve dështoi: ${e.message}`; }
    return '';
  };
  const scopeChip = (kind, item) => {
    const sc = scopeOf(kind, item);
    return <Chip size="small" label={sc.all ? 'Të gjitha projektet' : `${sc.ids.length} projekt${sc.ids.length === 1 ? '' : 'e'}`} variant="outlined" sx={{ fontSize:9, height:15, borderColor: sc.all ? 'divider' : 'primary.main', color: sc.all ? 'text.secondary' : 'primary.main' }} />;
  };
  /** Kontrolli i përbashkët: «Të gjitha projektet» / «Projekte të caktuara» + multi-select. */
  const ProjectScopeField = ({ kind, item, onChange }) => {
    const sc = scopeOf(kind, item);
    const selected = projects.filter(p => sc.ids.map(Number).includes(Number(p.id)));
    return (
      <Grid item xs={12}>
        <Typography variant="caption" fontWeight={700} color="text.secondary">Zbatohet për projektet</Typography>
        <RadioGroup row value={sc.all ? 'all' : 'specific'}
          onChange={e => onChange({ AppliesToAllProjects: e.target.value === 'all', ProjectIds: e.target.value === 'all' ? [] : sc.ids })}>
          <FormControlLabel value="all" control={<Radio size="small" />} label={<Typography variant="body2">Të gjitha projektet</Typography>} />
          <FormControlLabel value="specific" control={<Radio size="small" />} label={<Typography variant="body2">Projekte të caktuara</Typography>} />
        </RadioGroup>
        {!sc.all && (
          <Autocomplete multiple size="small" options={projects} value={selected} getOptionLabel={o => o.name}
            isOptionEqualToValue={(o, v) => Number(o.id) === Number(v.id)}
            onChange={(_, v) => onChange({ AppliesToAllProjects: false, ProjectIds: v.map(x => x.id) })}
            renderTags={(val, getTagProps) => val.map((o, i) => <Chip size="small" label={o.name} {...getTagProps({ index: i })} key={o.id} />)}
            renderInput={params => <TextField {...params} label="Projektet *" placeholder="Zgjidh…" inputProps={{ ...params.inputProps, 'data-testid': 'policy-projects' }} />}
            sx={{ mt: 0.5 }} />
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display:'block', mt:0.5 }}>
          Vlen për punonjësit që janë në këto projekte në datat e rregullit (caktimet me data). Zbatohet nga serveri.
        </Typography>
      </Grid>
    );
  };
  const saveBlackout = async () => {
    if (!editBlackout?.Name?.trim()||!editBlackout?.DateFrom||!editBlackout?.DateTo){ setSnack('Emri dhe datat janë të detyrueshme!'); return; }
    const scopeErr = validateScope(editBlackout);
    if (scopeErr) { setSnack(scopeErr); return; }
    const payload = { ...editBlackout }; delete payload.ProjectIds;
    let id = editBlackout.Id;
    if (editBlackout.Id===0) { const r = await apiService().post('leaveBlackouts', { ...payload, Id:undefined }); id = r?.data?.Id ?? r?.data?.id; }
    else                     await apiService().put('leaveBlackouts', payload);
    const se = await saveScope('blackout', id, editBlackout);
    setEditBlackout(null); load(); setSnack(se || 'Periudha e bllokuar u ruajt.');
  };
  const deleteBlackoutFn = async () => {
    await apiService().delete('leaveBlackouts', deleteBlackout);
    setDeleteBlackout(null); load(); setSnack('Periudha u fshi.');
  };

  // ─── export CSV ────────────────────────────────────────────────────────────
  const exportCSV = () => {
    const rows = [['Data','Emri','Lloji','Kombëtare']];
    holidays.filter(h=>h.Year===year).forEach(h=>rows.push([h.Date,h.Name,h.Type,h.IsNational?'Po':'Jo']));
    const csv = rows.map(r=>r.map(c=>`"${c}"`).join(',')).join('\n');
    const a = document.createElement('a'); a.href='data:text/csv;charset=utf-8,'+encodeURIComponent(csv);
    a.download=`pushimet-zyrtare-${year}.csv`; a.click();
  };

  // ─── active blackout ───────────────────────────────────────────────────────
  const activeBlackouts = blackouts.filter(b => dateInRange(today, b.DateFrom, b.DateTo));

  // ─── new leave request from calendar double-click ─────────────────────────
  const onDayDoubleClick = (dateStr) => {
    setNewLeaveForm({ WorkerId: '', LeaveTypeId: leaveTypes[0]?.Id ? String(leaveTypes[0].Id) : '', StartDate: dateStr, EndDate: dateStr, Reason: '' });
    setNewLeaveOpen(true);
  };

  const doCreateLeave = async () => {
    if (!newLeaveForm.WorkerId || !newLeaveForm.LeaveTypeId || !newLeaveForm.StartDate || !newLeaveForm.EndDate) {
      setSnack('Plotëso punëtorin, llojin dhe datat!'); return;
    }
    setNewLeaveSaving(true);
    const days = Math.max(1, Math.round((new Date(newLeaveForm.EndDate) - new Date(newLeaveForm.StartDate)) / 86400000) + 1);
    const r = await apiService().post('leaveRequests', {
      WorkerId:    Number(newLeaveForm.WorkerId),
      LeaveTypeId: Number(newLeaveForm.LeaveTypeId),
      StartDate:   newLeaveForm.StartDate,
      EndDate:     newLeaveForm.EndDate,
      DaysCount:   days,
      Reason:      newLeaveForm.Reason || null,
      Status:      'pending',
      RequestedAt: today,
    });
    setNewLeaveSaving(false);
    if (r?.success) { setSnack('Kërkesa për pushim u shtua!'); setNewLeaveOpen(false); load(); }
    else setSnack('Gabim gjatë shtimit të kërkesës!');
  };

  return (
    <Box>

      {/* ── Page Header ── */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        sx={{ mb: 3 }}
        gap={2}
      >
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Box sx={{ p: 1.2, borderRadius: 2.5, bgcolor: alpha('#3E8FA3', 0.1), display: 'flex', alignItems: 'center' }}>
            <CalendarMonthIcon sx={{ fontSize: 24, color: '#3E8FA3' }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={800} letterSpacing={-0.5} lineHeight={1.2}>
              Kalendari i Pushimeve
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem' }}>
              Pushimet zyrtare, bllokimet dhe kërkesat e stafit
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          {pendingLeaves.length > 0 && (
            <Chip
              size="small"
              label={`${pendingLeaves.length} në pritje`}
              icon={<HourglassTopIcon sx={{ fontSize: '12px !important' }} />}
              onClick={() => { setMainTab(0); setTab(2); }}
              color="warning"
              sx={{ fontWeight: 700, cursor: 'pointer', fontSize: '0.75rem' }}
            />
          )}
          <Button
            size="small" variant="outlined"
            startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
            onClick={exportCSV}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, fontSize: '0.82rem' }}
          >
            CSV
          </Button>
        </Stack>
      </Stack>

      {/* ── KPI Cards ── */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        {[
          {
            label: 'Pushime zyrtare', sub: `viti ${year}`,
            value: holidays.filter(h => h.Year === year).length,
            color: '#ef4444', bg: '#fef2f2', icon: <EventBusyIcon />,
          },
          {
            label: 'Ditë pune', sub: `pa festë · ${year}`,
            value: (() => {
              let w = 0;
              for (let m = 0; m < 12; m++) {
                const d = daysInMonth(year, m);
                for (let dd = 1; dd <= d; dd++) {
                  const ds = isoDate(year, m, dd);
                  if (!isWeekend(year, m, dd) && !holidays.some(h => h.Date === ds)) w++;
                }
              }
              return w;
            })(),
            color: '#06b6d4', bg: '#ecfeff', icon: <WorkHistoryIcon />,
          },
          {
            label: 'Aprovuara', sub: 'gjithsej',
            value: leaves.filter(l => l.Status === 'approved').length,
            color: '#10b981', bg: '#ecfdf5', icon: <CheckCircleIcon />,
          },
          {
            label: 'Në pritje', sub: 'aktive',
            value: pendingLeaves.length,
            color: '#f59e0b', bg: '#fffbeb', icon: <HourglassTopIcon />,
          },
        ].map(s => (
          <Card key={s.label} elevation={0} sx={{
            flex: 1, minWidth: 140,
            borderRadius: 2.5, border: '1px solid', borderColor: alpha(s.color, 0.2),
            bgcolor: s.bg,
          }}>
            <CardContent sx={{ p: '14px 18px !important' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="h4" fontWeight={800} sx={{ color: s.color, lineHeight: 1, mb: 0.3 }}>
                    {s.value}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ fontSize: '0.78rem', lineHeight: 1.3 }}>
                    {s.label}
                  </Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.68rem' }}>
                    {s.sub}
                  </Typography>
                </Box>
                <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha(s.color, 0.12), display: 'flex', alignItems: 'center' }}>
                  {React.cloneElement(s.icon, { sx: { fontSize: 20, color: s.color } })}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* ══ MAIN TABS + MONTH NAV ═══════════════════════════════════════════ */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center"
          sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
          <Tabs value={mainTab} onChange={(_, v) => setMainTab(v)}
            sx={{
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', minHeight: 44, gap: 0.75 },
              '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
            }}>
            <Tab label="Kalendari i Pushimeve" icon={<CalendarMonthIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
            <Tab label="Prezenca Mujore Stafit" icon={<GridViewIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
            <Tab label="Pushimet — Kalendarë" icon={<BeachAccessIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
          </Tabs>
          {/* Month navigation — visible on md+ */}
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ pr: 1, display: { xs: 'none', sm: 'flex' } }}>
            <IconButton size="small" onClick={prevMonth}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, '&:hover': { bgcolor: 'grey.100' } }}>
              <ChevronLeftIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.8,
              px: 1.5, py: 0.5, borderRadius: 1.5,
              border: '1px solid', borderColor: 'divider',
              minWidth: 130, justifyContent: 'center',
            }}>
              <Typography variant="body2" fontWeight={800} letterSpacing={-0.3} textTransform="capitalize" sx={{ fontSize: '0.82rem' }}>
                {MONTHS_ALB[month]} {year}
              </Typography>
            </Box>
            <IconButton size="small" onClick={nextMonth}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, '&:hover': { bgcolor: 'grey.100' } }}>
              <ChevronRightIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      {/* Active blackout alert */}
      {activeBlackouts.length > 0 && (
        <Alert severity="warning" icon={<BlockIcon />} sx={{ mb:2, borderRadius:2 }}>
          <strong>Periudhë aktive e bllokuar:</strong>{' '}
          {activeBlackouts.map(b=>`${b.Name} (${fmtDate(b.DateFrom)} → ${fmtDate(b.DateTo)})`).join(' | ')}
        </Alert>
      )}

      {mainTab === 0 && (<>

      <Grid container spacing={2} alignItems="flex-start">
        {/* ── Calendar column ──────────────────────────────────────────────── */}
        <Grid item xs={12} lg={8}>
          <Paper elevation={0} sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>

            {/* Calendar header bar */}
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider',
              bgcolor: '#f8fafc',
            }}>
              {/* Mobile month navigation (hidden on sm+) */}
              <Stack direction="row" alignItems="center" spacing={0.5}
                sx={{ display: { xs: 'flex', sm: 'none' } }}>
                <IconButton size="small" onClick={prevMonth} sx={{ p: 0.4 }}>
                  <ChevronLeftIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <Typography variant="subtitle2" fontWeight={800} sx={{ color: '#1E293B', fontSize: '0.82rem', textTransform: 'capitalize', minWidth: 100, textAlign: 'center' }}>
                  {MONTHS_ALB[month]} {year}
                </Typography>
                <IconButton size="small" onClick={nextMonth} sx={{ p: 0.4 }}>
                  <ChevronRightIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>
              {/* Desktop: just month title */}
              <Typography variant="subtitle2" fontWeight={800} sx={{ color: '#1E293B', fontSize: '0.85rem', textTransform: 'capitalize', display: { xs: 'none', sm: 'block' } }}>
                {MONTHS_ALB[month]} {year}
              </Typography>
              <Stack direction="row" spacing={0.75} alignItems="center">
                {[
                  { label: `${monthHolidays.length} festë`,  color: '#DC2626', bg: '#FEF2F2', border: '#FECACA' },
                  { label: `${monthBlackouts.length} bllok`, color: '#D97706', bg: '#FFFBEB', border: '#FDE68A' },
                  { label: `${monthLeaves.length} pushim`,   color: '#059669', bg: '#ECFDF5', border: '#A7F3D0' },
                  ...(pendingLeaves.length > 0 ? [{ label: `${pendingLeaves.length} pritje`, color: '#2563EB', bg: '#EFF6FF', border: '#BFDBFE', bold: true }] : []),
                ].map(s => (
                  <Box key={s.label} sx={{ px: 1, py: 0.25, borderRadius: 5, bgcolor: s.bg, border: `1px solid ${s.border}` }}>
                    <Typography sx={{ fontSize: 10, fontWeight: s.bold ? 800 : 600, color: s.color }}>{s.label}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>

            {/* Legend strip */}
            <Box sx={{ px: 2, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#FAFBFC' }}>
              <Stack direction="row" spacing={1.5} flexWrap="wrap">
                {[
                  { label: 'Festë zyrtare',      color: COLORS.holiday.border  },
                  { label: 'Periudhë bllokuar',  color: COLORS.blackout.border },
                  { label: 'Pushim aprovuar',     color: COLORS.approved.border },
                  { label: 'Kërkesë në pritje',  color: COLORS.pending.border  },
                  { label: 'Sot',                 color: COLORS.today.border    },
                ].map(l => (
                  <Stack key={l.label} direction="row" alignItems="center" spacing={0.5}>
                    <Box sx={{ width: 8, height: 8, borderRadius: 0.3, bgcolor: l.color, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 9.5, color: '#64748B', fontWeight: 500 }}>{l.label}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>

            {/* Calendar grid */}
            <MonthView year={year} month={month} holidays={holidays} blackouts={blackouts} leaves={monthLeaves} onDayClick={setDayDetail} />
          </Paper>
        </Grid>

        {/* ── Right panel ───────────────────────────────────────────────────── */}
        <Grid item xs={12} lg={4}>
          <Paper elevation={0} sx={{ borderRadius:2.5, border:'1px solid', borderColor:'divider', overflow:'hidden', position:'sticky', top:16 }}>
            <Tabs value={tab} onChange={(_,v)=>setTab(v)} variant="fullWidth"
              sx={{
                borderBottom:'1px solid', borderColor:'divider',
                bgcolor:'grey.50',
                '& .MuiTab-root':{ minHeight:40, textTransform:'none', fontWeight:700, fontSize:'0.78rem' },
                '& .MuiTabs-indicator':{ height:3, borderRadius:'3px 3px 0 0' },
              }}>
              <Tab label="Zyrtare" />
              <Tab label="Bllokuara" />
              <Tab label={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <span>Kërkesat</span>
                  {pendingLeaves.length > 0 && (
                    <Box sx={{ bgcolor:'warning.main', color:'#fff', borderRadius:'50%', width:16, height:16, fontSize:10, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700 }}>
                      {pendingLeaves.length}
                    </Box>
                  )}
                </Stack>
              } />
            </Tabs>

            {/* ── Tab 0: Holidays ─────────────────────────────────────────── */}
            {tab === 0 && (
              <Box>
                <Box sx={{ px:2, py:1.25, borderBottom:'1px solid', borderColor:'divider', display:'flex', justifyContent:'space-between', alignItems:'center', bgcolor:'grey.50' }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary">
                    {holidays.filter(h=>h.Year===year).length} pushime — {year}
                  </Typography>
                  <Button size="small" variant="contained" startIcon={<AddIcon sx={{ fontSize:'14px !important' }} />}
                    onClick={() => setEditHoliday({ ...BLANK_H, Year:year })}
                    sx={{ textTransform:'none', fontSize:'0.72rem', borderRadius:1.5 }}>Shto</Button>
                </Box>
                <Box sx={{ maxHeight:440, overflowY:'auto' }}>
                  {holidays
                    .filter(h => h.Year===year)
                    .sort((a,b) => a.Date.localeCompare(b.Date))
                    .map(h => (
                      <Box key={h.Id} sx={{ px:2, py:1, borderBottom:'1px solid', borderColor:'divider', '&:hover':{ bgcolor:'grey.50' }, transition:'background .1s' }}>
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                          <Box sx={{ flex:1, minWidth:0 }}>
                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb:0.25 }}>
                              <Typography variant="caption" fontWeight={800} color="error.main" sx={{ fontFamily:'monospace', flexShrink:0 }}>{h.Date.slice(5)}</Typography>
                              {h.IsNational && <Chip label="Komb." size="small" color="error" sx={{ fontSize:9, height:15 }} />}
                              {h.Type==='movable' && <Chip label="Lëvizëse" size="small" variant="outlined" sx={{ fontSize:9, height:15 }} />}
                              {scopeChip('holiday', h)}
                            </Stack>
                            <Typography variant="body2" fontWeight={600} sx={{ lineHeight:1.3, fontSize:'0.8rem' }}>{h.Name}</Typography>
                          </Box>
                          <Stack direction="row" spacing={0.25} sx={{ flexShrink:0, ml:0.5 }}>
                            <IconButton size="small" onClick={() => setEditHoliday({ ...h })}><EditIcon sx={{ fontSize:14 }} /></IconButton>
                            <IconButton size="small" color="error" onClick={() => setDeleteHoliday(h.Id)}><DeleteIcon sx={{ fontSize:14 }} /></IconButton>
                          </Stack>
                        </Stack>
                      </Box>
                    ))}
                </Box>
              </Box>
            )}

            {/* ── Tab 1: Blackouts ────────────────────────────────────────── */}
            {tab === 1 && (
              <Box>
                <Box sx={{ px:2, py:1.25, borderBottom:'1px solid', borderColor:'divider', display:'flex', justifyContent:'space-between', alignItems:'center', bgcolor:'grey.50' }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary">{blackouts.length} periudha</Typography>
                  <Button size="small" variant="contained" color="warning" startIcon={<AddIcon sx={{ fontSize:'14px !important' }} />}
                    onClick={() => setEditBlackout({ ...BLANK_B })}
                    sx={{ textTransform:'none', fontSize:'0.72rem', borderRadius:1.5 }}>Shto</Button>
                </Box>
                <Box sx={{ maxHeight:440, overflowY:'auto' }}>
                  {blackouts.sort((a,b)=>a.DateFrom.localeCompare(b.DateFrom)).map(b => (
                    <Box key={b.Id} sx={{ px:2, py:1.25, borderBottom:'1px solid', borderColor:'divider', '&:hover':{ bgcolor:'grey.50' }, transition:'background .1s' }}>
                      <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                        <Box sx={{ flex:1, minWidth:0 }}>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb:0.25 }}>
                            <Chip size="small" label={BLACKOUT_TYPES[b.Type]?.label||b.Type}
                              sx={{ fontSize:9, height:15, bgcolor:(BLACKOUT_TYPES[b.Type]?.color||'#666')+'20', color:BLACKOUT_TYPES[b.Type]?.color||'#666' }} />
                            {b.Strict && <Chip size="small" label="Strikt" color="error" sx={{ fontSize:9, height:15 }} />}
                            {scopeChip('blackout', b)}
                          </Stack>
                          <Typography variant="body2" fontWeight={700} sx={{ lineHeight:1.2, fontSize:'0.8rem' }}>{b.Name}</Typography>
                          <Typography variant="caption" sx={{ color:'text.secondary', fontFamily:'monospace', fontSize:10 }}>
                            {fmtDate(b.DateFrom)} → {fmtDate(b.DateTo)}
                          </Typography>
                          {b.Reason && <Typography variant="caption" color="text.secondary" sx={{ display:'block', fontSize:10, mt:0.25 }}>{b.Reason}</Typography>}
                        </Box>
                        <Stack direction="row" spacing={0.25} sx={{ flexShrink:0, ml:0.5 }}>
                          <IconButton size="small" onClick={() => setEditBlackout({ ...b, Departments:b.Departments||[] })}><EditIcon sx={{ fontSize:14 }} /></IconButton>
                          <IconButton size="small" color="error" onClick={() => setDeleteBlackout(b.Id)}><DeleteIcon sx={{ fontSize:14 }} /></IconButton>
                        </Stack>
                      </Stack>
                    </Box>
                  ))}
                  {blackouts.length===0 && (
                    <Box sx={{ p:3, textAlign:'center' }}><Typography variant="body2" color="text.secondary">Nuk ka periudha të bllokuara.</Typography></Box>
                  )}
                </Box>
                <Box sx={{ px:2, py:1, bgcolor:'#fffbeb', borderTop:'1px solid', borderColor:'#fde68a' }}>
                  <Stack direction="row" spacing={0.75} alignItems="flex-start">
                    <InfoOutlinedIcon sx={{ fontSize:13, color:'warning.dark', mt:0.15, flexShrink:0 }} />
                    <Typography variant="caption" color="warning.dark" sx={{ fontSize:10 }}>
                      <strong>Strikt</strong> = refuzohet automatikisht. <strong>Jo-strikt</strong> = paralajmërohet menaxheri.
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            )}

            {/* ── Tab 2: Kërkesat ─────────────────────────────────────────── */}
            {tab === 2 && (
              <Box>
                <Box sx={{ px:1.5, py:1, borderBottom:'1px solid', borderColor:'divider', display:'flex', alignItems:'center', gap:1, bgcolor:'grey.50' }}>
                  <FilterListIcon sx={{ fontSize:14, color:'text.secondary' }} />
                  {['all','pending','approved','rejected'].map(s => (
                    <Chip key={s} size="small" clickable
                      label={s==='all'?'Të gjitha':STATUS_META[s]?.label}
                      color={s==='all'&&filterStatus==='all'?'primary':filterStatus===s?STATUS_META[s]?.color||'default':'default'}
                      variant={filterStatus===s?'filled':'outlined'}
                      onClick={() => setFilterStatus(s)}
                      sx={{ fontSize:10, height:22 }} />
                  ))}
                </Box>

                {filterStatus!=='rejected' && pendingLeaves.length > 0 && (
                  <Alert severity="warning" sx={{ mx:1.5, mt:1, py:0.5, fontSize:12, borderRadius:1.5 }}>
                    <strong>{pendingLeaves.length} kërkesa</strong> presin aprovimin tuaj
                  </Alert>
                )}

                <Box sx={{ maxHeight:420, overflowY:'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor:'grey.50' }}>
                        <TableCell sx={{ fontWeight:700, fontSize:11, py:0.75 }}>Punëtori</TableCell>
                        <TableCell sx={{ fontWeight:700, fontSize:11, py:0.75 }}>Periudha</TableCell>
                        <TableCell sx={{ fontWeight:700, fontSize:11, py:0.75 }}>Statusi</TableCell>
                        <TableCell align="right" sx={{ py:0.75 }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredLeaves.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py:3 }}>
                            <Typography variant="body2" color="text.secondary">Nuk ka kërkesa.</Typography>
                          </TableCell>
                        </TableRow>
                      ) : filteredLeaves.sort((a,b)=>a.Status==='pending'?-1:1).map(req => (
                        <RequestRow key={req.Id} req={req} leaveTypes={leaveTypes}
                          onApprove={setApproveReq} onReject={setRejectReq}
                          onDetail={setDetailReq} saving={saving} />
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
      </>)}

      {/* ── TAB 1: Prezenca Mujore Stafit ── */}
      {mainTab === 1 && (
        <Box>
          {/* Control bar */}
          <Paper elevation={0} sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider', mb: 1.5, overflow: 'hidden' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between" gap={1} sx={{ px: 1.5, py: 1 }}>
              {/* Search */}
              <TextField
                size="small" placeholder="Kërko punëtor…" value={presenceSearch}
                onChange={e => setPresenceSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} /></InputAdornment> }}
                sx={{ width: { xs: '100%', sm: 220 }, '& .MuiInputBase-root': { borderRadius: 1.5, fontSize: '0.82rem', height: 32 } }}
              />
              {/* Legend — collapsed on mobile */}
              <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center"
                sx={{ display: { xs: 'none', md: 'flex' } }}>
                {[
                  { k: 'present', label: 'Prezent',       Icon: CheckCircleIcon,  color: '#16A34A' },
                  { k: 'leave',   label: 'Pushim vjetor', Icon: BeachAccessIcon,   color: '#2563EB' },
                  { k: 'sick',    label: 'Mjekësor',      Icon: LocalHospitalIcon, color: '#EA580C' },
                  { k: 'pending', label: 'Pritje',        Icon: HourglassTopIcon,  color: '#CA8A04' },
                  { k: 'absent',  label: 'Mungesë',       Icon: CancelIcon,        color: '#E11D48' },
                  { k: 'holiday', label: 'Festë',         Icon: EventBusyIcon,     color: '#DC2626' },
                ].map(({ k, label, Icon, color }) => (
                  <Stack key={k} direction="row" alignItems="center" spacing={0.4}>
                    <Icon sx={{ fontSize: 13, color, flexShrink: 0 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', whiteSpace: 'nowrap' }}>
                      {label}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
              {/* Stats + actions */}
              <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
                {presenceLoading && <CircularProgress size={14} />}
                <Typography variant="caption" color="text.disabled" sx={{ whiteSpace: 'nowrap' }}>
                  {workers.length} punëtorë · {presenceEvents.length} regj.
                </Typography>
                {/* Group by department toggle */}
                <Tooltip title={groupByDept ? 'Çaktivazo grupin sipas departamentit' : 'Grupo sipas departamentit'}>
                  <Button
                    size="small"
                    variant={groupByDept ? 'contained' : 'outlined'}
                    color={groupByDept ? 'primary' : 'inherit'}
                    startIcon={<GroupsIcon sx={{ fontSize: 14 }} />}
                    onClick={() => setGroupByDept(v => !v)}
                    sx={{ textTransform: 'none', fontSize: '0.73rem', borderRadius: 1.5, height: 30, whiteSpace: 'nowrap' }}
                  >
                    Grup
                  </Button>
                </Tooltip>
                <Button size="small" variant="outlined" startIcon={<FileDownloadIcon sx={{ fontSize: 14 }} />}
                  onClick={() => {
                    const days = daysInMonth(year, month);
                    const CELL_STYLES = {
                      present: { fgColor: { rgb: 'D1FAE5' }, font: { rgb: '16A34A' }, val: '✓' },
                      leave:   { fgColor: { rgb: 'DBEAFE' }, font: { rgb: '2563EB' }, val: 'L' },
                      sick:    { fgColor: { rgb: 'FFF7ED' }, font: { rgb: 'EA580C' }, val: 'Sj' },
                      pending: { fgColor: { rgb: 'FEFCE8' }, font: { rgb: 'CA8A04' }, val: 'Pr' },
                      absent:  { fgColor: { rgb: 'FFF1F2' }, font: { rgb: 'E11D48' }, val: '✗' },
                      holiday: { fgColor: { rgb: 'FEF2F2' }, font: { rgb: 'DC2626' }, val: 'F' },
                      weekend: { fgColor: { rgb: 'F1F5F9' }, font: { rgb: '94A3B8' }, val: '' },
                      future:  { fgColor: { rgb: 'F9FAFB' }, font: { rgb: 'D1D5DB' }, val: '' },
                    };
                    const mkCell = (v, fgRgb, fontRgb, bold = false, border = false) => ({
                      v, t: 's',
                      s: {
                        fill: { patternType: 'solid', fgColor: { rgb: fgRgb } },
                        font: { bold, color: { rgb: fontRgb }, sz: 9, name: 'Calibri' },
                        alignment: { horizontal: 'center', vertical: 'center', wrapText: false },
                        border: border ? {
                          top:    { style: 'thin', color: { rgb: 'E2E8F0' } },
                          bottom: { style: 'thin', color: { rgb: 'E2E8F0' } },
                          left:   { style: 'thin', color: { rgb: 'E2E8F0' } },
                          right:  { style: 'thin', color: { rgb: 'E2E8F0' } },
                        } : {},
                      },
                    });

                    // ── Title row ──
                    const titleCell = { v: `Prezenca Mujore — ${MONTHS_ALB[month]} ${year}`, t: 's',
                      s: { font: { bold: true, sz: 13, color: { rgb: '1E293B' }, name: 'Calibri' },
                           fill: { patternType: 'solid', fgColor: { rgb: 'EEF2FF' } },
                           alignment: { horizontal: 'left', vertical: 'center' } } };

                    // ── Header row ──
                    const hStyle = { fill: { patternType: 'solid', fgColor: { rgb: '1E293B' } },
                      font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 8, name: 'Calibri' },
                      alignment: { horizontal: 'center', vertical: 'center' } };
                    const hdr = [
                      { v: 'Punëtori', t: 's', s: { ...hStyle, alignment: { horizontal: 'left', vertical: 'center' } } },
                      ...Array.from({ length: days }, (_, i) => {
                        const d = i + 1;
                        const wd = new Date(year, month, d).toLocaleString('sq-AL', { weekday: 'short' }).slice(0,2).toUpperCase();
                        const isWE = isWeekend(year, month, d);
                        const isH  = holidays.some(h => h.Date === isoDate(year, month, d));
                        return { v: `${d}\n${wd}`, t: 's', s: { ...hStyle,
                          fill: { patternType: 'solid', fgColor: { rgb: isH ? '7F1D1D' : isWE ? '334155' : '1E293B' } },
                          alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
                        }};
                      }),
                      ...['P','L','Sj','M','F','Tj'].map((lbl, li) => {
                        const colors = ['16A34A','2563EB','EA580C','E11D48','DC2626','64748B'];
                        return { v: lbl, t: 's', s: { ...hStyle,
                          fill: { patternType: 'solid', fgColor: { rgb: '0F172A' } },
                          font: { bold: true, color: { rgb: colors[li] }, sz: 9, name: 'Calibri' },
                          alignment: { horizontal: 'center', vertical: 'center' },
                        }};
                      }),
                    ];

                    // ── Data rows ──
                    const todayStr = new Date().toISOString().slice(0, 10);
                    const dataRows = workers.map(w => {
                      const wid = String(w.Id ?? w.id);
                      let pC=0, lC=0, sC=0, mC=0, hC=0;
                      const dayCells = Array.from({ length: days }, (_, i) => {
                        const d = i + 1;
                        const ds = isoDate(year, month, d);
                        const isWE = isWeekend(year, month, d);
                        const isH  = holidays.some(h => h.Date === ds);
                        const isFut = ds > todayStr;
                        if (isH)  { hC++; const cs = CELL_STYLES.holiday; return mkCell(cs.val, cs.fgColor.rgb, cs.font.rgb, false, true); }
                        if (isWE) { const cs = CELL_STYLES.weekend; return mkCell(cs.val, cs.fgColor.rgb, cs.font.rgb, false, true); }
                        if (isFut){ const cs = CELL_STYLES.future;  return mkCell(cs.val, cs.fgColor.rgb, cs.font.rgb, false, true); }
                        const lv = leaves.find(lx => String(lx.WorkerId??lx.workerId)===wid && ds>=lx.StartDate && ds<=lx.EndDate && lx.Status!=='rejected');
                        if (lv) {
                          const isSick = SICK_TYPE_IDS && SICK_TYPE_IDS.has(lv.LeaveTypeId);
                          const isPend = lv.Status === 'pending';
                          if (isSick) { sC++; const cs = CELL_STYLES.sick;    return mkCell(cs.val, cs.fgColor.rgb, cs.font.rgb, false, true); }
                          if (isPend) { lC++; const cs = CELL_STYLES.pending; return mkCell(cs.val, cs.fgColor.rgb, cs.font.rgb, false, true); }
                          lC++; const cs = CELL_STYLES.leave; return mkCell(cs.val, cs.fgColor.rgb, cs.font.rgb, false, true);
                        }
                        const evts = (presenceEvents||[]).filter(ev => String(ev.WorkerId??ev.workerId)===wid && (ev.At||'').split('T')[0]===ds);
                        if (evts.length > 0) { pC++; const cs = CELL_STYLES.present; return mkCell(cs.val, cs.fgColor.rgb, cs.font.rgb, true, true); }
                        mC++; const cs = CELL_STYLES.absent; return mkCell(cs.val, cs.fgColor.rgb, cs.font.rgb, false, true);
                      });
                      const totalW = pC + lC + sC + mC;
                      const nameCell = { v: `${w.FirstName||w.Name} ${w.LastName||w.Surname}`, t: 's',
                        s: { font: { bold: true, sz: 9, color: { rgb: '1E293B' }, name: 'Calibri' },
                             fill: { patternType: 'solid', fgColor: { rgb: 'FFFFFF' } },
                             alignment: { horizontal: 'left', vertical: 'center' },
                             border: { right: { style: 'medium', color: { rgb: 'E2E8F0' } } } } };
                      const sumCols = [
                        { v: pC, rgb: 'D1FAE5', font: '16A34A' },
                        { v: lC, rgb: 'EFF6FF', font: '2563EB' },
                        { v: sC, rgb: 'FFF7ED', font: 'EA580C' },
                        { v: mC, rgb: 'FFF1F2', font: 'E11D48' },
                        { v: hC, rgb: 'FEF2F2', font: 'DC2626' },
                        { v: totalW - pC, rgb: 'F8FAFC', font: '64748B' },
                      ].map(({ v, rgb, font }) => ({
                        v, t: 'n',
                        s: { fill: { patternType: 'solid', fgColor: { rgb } },
                             font: { bold: true, sz: 10, color: { rgb: font }, name: 'Calibri' },
                             alignment: { horizontal: 'center', vertical: 'center' } },
                      }));
                      return [nameCell, ...dayCells, ...sumCols];
                    });

                    // ── Totals row ──
                    const totStyle = { fill: { patternType: 'solid', fgColor: { rgb: 'EFF6FF' } },
                      font: { bold: true, sz: 9, color: { rgb: '0369A1' }, name: 'Calibri' },
                      alignment: { horizontal: 'center', vertical: 'center' } };
                    const totRow = [
                      { v: 'TOTAL / Ditë', t: 's', s: { ...totStyle, alignment: { horizontal: 'left', vertical: 'center' }, font: { ...totStyle.font, color: { rgb: '1E293B' } } } },
                      ...Array.from({ length: days }, (_, i) => {
                        const d = i + 1;
                        const ds = isoDate(year, month, d);
                        const isWE = isWeekend(year, month, d);
                        const isH  = holidays.some(h => h.Date === ds);
                        if (isWE || isH) return { v: '', t: 's', s: { fill: { patternType: 'solid', fgColor: { rgb: isH ? 'FEF2F2' : 'F1F5F9' } }, font: { sz: 8 } } };
                        const cnt = workers.reduce((acc, w) => {
                          const wid = String(w.Id ?? w.id);
                          const evts = (presenceEvents||[]).filter(ev => String(ev.WorkerId??ev.workerId)===wid && (ev.At||'').split('T')[0]===ds);
                          return acc + (evts.length > 0 ? 1 : 0);
                        }, 0);
                        return { v: cnt, t: 'n', s: totStyle };
                      }),
                      ...['','','','','',''].map(() => ({ v: '', t: 's', s: { fill: { patternType: 'solid', fgColor: { rgb: 'EFF6FF' } } } })),
                    ];

                    // ── Build sheet ──
                    const colCount = 1 + days + 6;
                    const aoa = [[titleCell, ...Array(colCount - 1).fill({ v: '', t: 's', s: { fill: { patternType: 'solid', fgColor: { rgb: 'EEF2FF' } } } })], hdr, ...dataRows, totRow];
                    const ws = XLSX.utils.aoa_to_sheet(aoa);

                    // Column widths
                    ws['!cols'] = [{ wch: 22 }, ...Array(days).fill({ wch: 4 }), ...Array(6).fill({ wch: 5 })];
                    // Row heights
                    ws['!rows'] = [{ hpx: 24 }, { hpx: 28 }, ...dataRows.map(() => ({ hpx: 18 })), { hpx: 18 }];
                    // Merge title across all columns
                    ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } }];
                    // Freeze first col + 2 header rows
                    ws['!freeze'] = { xSplit: 1, ySplit: 2 };

                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, `${MONTHS_ALB[month]} ${year}`);
                    XLSX.writeFile(wb, `prezenca_${MONTHS_ALB[month]}_${year}.xlsx`);
                  }}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', borderRadius: 1.5, height: 30, whiteSpace: 'nowrap' }}>
                  Excel
                </Button>
              </Stack>
            </Stack>
          </Paper>

          {/* Grid */}
          <Paper elevation={0} sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto', overflowX: 'auto' }}>
              <MonthlyPresenceGrid
                year={year} month={month} workers={workers} leaves={leaves}
                holidays={holidays} presenceEvents={presenceEvents} search={presenceSearch}
                departments={departments} groupByDept={groupByDept}
              />
            </TableContainer>
          </Paper>
        </Box>
      )}

      {/* ══ TAB 2: Pushimet — Kalendarë (Big Calendar View) ═══════════════════ */}
      {mainTab === 2 && (
        <Paper elevation={0} sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
          <BigCalendarView
            year={year} month={month}
            leaves={leaves} leaveTypes={leaveTypes} workers={workers}
            onDayDoubleClick={onDayDoubleClick}
          />
        </Paper>
      )}

      {/* ══ Day detail dialog ══════════════════════════════════════════════════ */}
      <Dialog open={!!dayDetail} onClose={() => setDayDetail(null)} maxWidth="sm" fullWidth PaperProps={{ sx:{ borderRadius:2 } }}>
        <DialogTitle sx={{ fontWeight:700, pb:1 }}>
          {dayDetail?.dateStr && (() => { const [y,m,d]=dayDetail.dateStr.split('-').map(Number); return `${d} ${MONTHS_ALB[m-1]} ${y}`; })()}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt:1.5 }}>
          {dayDetail?.dayHolidays?.length > 0 && (
            <Box sx={{ mb:1.5 }}>
              <Typography variant="caption" fontWeight={700} color="error.main" sx={{ display:'block', mb:0.5 }}>Pushime zyrtare</Typography>
              {dayDetail.dayHolidays.map(h => (
                <Alert key={h.Id} severity="error" sx={{ mb:0.5, py:0 }}>
                  <Typography variant="body2" fontWeight={600}>{h.Name}</Typography>
                  {h.IsNational && <Typography variant="caption">Festë kombëtare</Typography>}
                </Alert>
              ))}
            </Box>
          )}
          {dayDetail?.dayBlackouts?.length > 0 && (
            <Box sx={{ mb:1.5 }}>
              <Typography variant="caption" fontWeight={700} color="warning.dark" sx={{ display:'block', mb:0.5 }}>Periudha të bllokuara</Typography>
              {dayDetail.dayBlackouts.map(b => (
                <Alert key={b.Id} severity="warning" sx={{ mb:0.5, py:0 }}>
                  <Typography variant="body2" fontWeight={600}>{b.Name}</Typography>
                  {b.Strict && <Chip size="small" label="Strikt — pushimi NUK lejohet" color="error" sx={{ fontSize:10, mt:0.25 }} />}
                  {b.Reason && <Typography variant="caption" sx={{ display:'block', mt:0.25 }}>{b.Reason}</Typography>}
                </Alert>
              ))}
            </Box>
          )}
          {dayDetail?.dayLeaves?.length > 0 && (
            <Box>
              <Typography variant="caption" fontWeight={700} color="success.dark" sx={{ display:'block', mb:0.5 }}>
                Pushime të planifikuara ({dayDetail.dayLeaves.length})
              </Typography>
              {dayDetail.dayLeaves.map(l => {
                const meta = STATUS_META[l.Status]||STATUS_META.pending;
                const type = leaveTypes.find(t=>t.Id===l.LeaveTypeId);
                return (
                  <Box key={l.Id} sx={{ mb:1, p:1.25, borderRadius:1.5, border:'1px solid', borderColor:'divider', bgcolor: l.Status==='approved'?COLORS.approved.bg:l.Status==='rejected'?COLORS.rejected.bg:COLORS.pending.bg }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar sx={{ width:26, height:26, fontSize:10, bgcolor:'primary.main' }}>{workerInitials(l.WorkerName)}</Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={700}>{l.WorkerName}</Typography>
                          <Typography variant="caption" color="text.secondary">{type?.Name||'Pushim'} · {l.DaysCount} ditë</Typography>
                        </Box>
                      </Stack>
                      <Chip size="small" label={meta.label} color={meta.color} icon={meta.icon} variant="outlined" sx={{ fontSize:10 }} />
                    </Stack>
                    {l.Status === 'pending' && (
                      <Stack direction="row" spacing={1} sx={{ mt:1 }}>
                        <Button size="small" variant="contained" color="success" startIcon={<CheckCircleIcon />}
                          onClick={() => { setDayDetail(null); setApproveReq(l); }}
                          sx={{ textTransform:'none', flex:1, fontSize:'0.75rem' }}>Aprovo</Button>
                        <Button size="small" variant="outlined" color="error" startIcon={<CancelIcon />}
                          onClick={() => { setDayDetail(null); setRejectReq(l); }}
                          sx={{ textTransform:'none', flex:1, fontSize:'0.75rem' }}>Refuzo</Button>
                      </Stack>
                    )}
                    {l.ReviewNotes && <Typography variant="caption" color="text.secondary" sx={{ display:'block', mt:0.5 }}>Shënim: {l.ReviewNotes}</Typography>}
                  </Box>
                );
              })}
            </Box>
          )}
          {!dayDetail?.dayHolidays?.length && !dayDetail?.dayBlackouts?.length && !dayDetail?.dayLeaves?.length && (
            <Typography variant="body2" color="text.secondary" sx={{ py:1 }}>Nuk ka ngjarje për këtë ditë.</Typography>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDayDetail(null)}>Mbyll</Button></DialogActions>
      </Dialog>

      {/* ══ Approve confirm dialog ═════════════════════════════════════════════ */}
      <Dialog open={!!approveReq} onClose={() => setApproveReq(null)} maxWidth="xs" fullWidth PaperProps={{ sx:{ borderRadius:2 } }}>
        <DialogTitle sx={{ fontWeight:700 }}>Aprovo kërkesën</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt:2 }}>
          {approveReq && (
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Avatar sx={{ bgcolor:'success.main', width:36, height:36 }}><CheckCircleIcon /></Avatar>
                <Box>
                  <Typography fontWeight={700}>{approveReq.WorkerName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {leaveTypes.find(t=>t.Id===approveReq.LeaveTypeId)?.Name||'Pushim'} · {approveReq.DaysCount} ditë
                  </Typography>
                </Box>
              </Stack>
              <Box sx={{ p:1.5, bgcolor:'grey.50', borderRadius:1.5 }}>
                <Typography variant="caption" color="text.secondary">Periudha:</Typography>
                <Typography variant="body2" fontWeight={700} sx={{ fontFamily:'monospace' }}>
                  {fmtDate(approveReq.StartDate)} → {fmtDate(approveReq.EndDate)}
                </Typography>
                {approveReq.Reason && <Typography variant="caption" color="text.secondary" sx={{ display:'block', mt:0.5 }}>Arsyeja: {approveReq.Reason}</Typography>}
              </Box>
              <Alert severity="info" sx={{ py:0 }}>Duke aprovuar, punëtori do të njoftohet dhe bilanci do të përditësohet.</Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p:2, gap:1 }}>
          <Button onClick={() => setApproveReq(null)} variant="outlined" sx={{ borderRadius:1.5, textTransform:'none' }}>Anulo</Button>
          <Button onClick={doApprove} variant="contained" color="success" startIcon={saving?<CircularProgress size={14} color="inherit"/>:<CheckCircleIcon />}
            disabled={saving} sx={{ borderRadius:1.5, textTransform:'none' }}>
            {saving ? 'Duke aprovuar...' : 'Aprovo'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══ Reject dialog ══════════════════════════════════════════════════════ */}
      <Dialog open={!!rejectReq} onClose={() => { setRejectReq(null); setRejectNote(''); }} maxWidth="xs" fullWidth PaperProps={{ sx:{ borderRadius:2 } }}>
        <DialogTitle sx={{ fontWeight:700 }}>Refuzo kërkesën</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt:2 }}>
          {rejectReq && (
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Avatar sx={{ bgcolor:'error.main', width:36, height:36 }}><CancelIcon /></Avatar>
                <Box>
                  <Typography fontWeight={700}>{rejectReq.WorkerName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {leaveTypes.find(t=>t.Id===rejectReq.LeaveTypeId)?.Name||'Pushim'} · {rejectReq.DaysCount} ditë
                  </Typography>
                </Box>
              </Stack>
              <Box sx={{ p:1.5, bgcolor:'grey.50', borderRadius:1.5 }}>
                <Typography variant="caption" sx={{ fontFamily:'monospace' }}>
                  {fmtDate(rejectReq.StartDate)} → {fmtDate(rejectReq.EndDate)}
                </Typography>
              </Box>
              <TextField
                size="small" fullWidth multiline minRows={2}
                label="Arsyeja e refuzimit (opsionale)"
                value={rejectNote}
                onChange={e => setRejectNote(e.target.value)}
                placeholder="p.sh. Periudhë shumë e ngarkuar, mungon stafi tjetër..."
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p:2, gap:1 }}>
          <Button onClick={() => { setRejectReq(null); setRejectNote(''); }} variant="outlined" sx={{ borderRadius:1.5, textTransform:'none' }}>Anulo</Button>
          <Button onClick={doReject} variant="contained" color="error" startIcon={saving?<CircularProgress size={14} color="inherit"/>:<CancelIcon />}
            disabled={saving} sx={{ borderRadius:1.5, textTransform:'none' }}>
            {saving ? 'Duke refuzuar...' : 'Refuzo'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══ Detail (approved/rejected) dialog ═════════════════════════════════ */}
      <Dialog open={!!detailReq} onClose={() => setDetailReq(null)} maxWidth="xs" fullWidth PaperProps={{ sx:{ borderRadius:2 } }}>
        <DialogTitle sx={{ fontWeight:700, pb:1 }}>Detajet e kërkesës</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt:2 }}>
          {detailReq && (
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Avatar sx={{ bgcolor:'primary.main', width:36, height:36 }}>{workerInitials(detailReq.WorkerName)}</Avatar>
                <Box>
                  <Typography fontWeight={700}>{detailReq.WorkerName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {leaveTypes.find(t=>t.Id===detailReq.LeaveTypeId)?.Name||'Pushim'} · {detailReq.DaysCount} ditë
                  </Typography>
                </Box>
              </Stack>
              {[
                { label:'Periudha', value:`${fmtDate(detailReq.StartDate)} → ${fmtDate(detailReq.EndDate)}` },
                { label:'Statusi',  value: STATUS_META[detailReq.Status]?.label },
                { label:'Kërkuar',  value: fmtDate(detailReq.RequestedAt) },
                { label:'Shqyrtuar nga', value: detailReq.ReviewedBy||'—' },
                { label:'Data shqyrtimi', value: fmtDate(detailReq.ReviewedAt)||'—' },
                { label:'Arsyeja',  value: detailReq.Reason||'—' },
                { label:'Shënim',   value: detailReq.ReviewNotes||'—' },
              ].map(r => (
                <Stack key={r.label} direction="row" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">{r.label}</Typography>
                  <Typography variant="caption" fontWeight={600}>{r.value}</Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDetailReq(null)}>Mbyll</Button></DialogActions>
      </Dialog>

      {/* ══ Edit holiday dialog ════════════════════════════════════════════════ */}
      <Dialog open={!!editHoliday} onClose={() => setEditHoliday(null)} maxWidth="sm" fullWidth PaperProps={{ sx:{ borderRadius:2 } }}>
        <DialogTitle sx={{ fontWeight:700, pb:1 }}>{editHoliday?.Id===0?'Shto pushim zyrtar':'Edito pushimin zyrtar'}</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt:2 }}>
          {editHoliday && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField size="small" fullWidth type="date" label="Data" value={editHoliday.Date}
                  onChange={e=>setEditHoliday(p=>({...p,Date:e.target.value}))} InputLabelProps={{ shrink:true }} />
              </Grid>
              <Grid item xs={6}>
                <TextField select size="small" fullWidth label="Lloji" value={editHoliday.Type}
                  onChange={e=>setEditHoliday(p=>({...p,Type:e.target.value}))}>
                  <MenuItem value="fixed">Fiks</MenuItem>
                  <MenuItem value="movable">Lëvizës</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField size="small" fullWidth label="Emri shqip *" value={editHoliday.Name}
                  onChange={e=>setEditHoliday(p=>({...p,Name:e.target.value}))} />
              </Grid>
              <Grid item xs={12}>
                <TextField size="small" fullWidth label="Emri anglisht" value={editHoliday.NameEn||''}
                  onChange={e=>setEditHoliday(p=>({...p,NameEn:e.target.value}))} />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel control={<Switch checked={editHoliday.IsNational} onChange={e=>setEditHoliday(p=>({...p,IsNational:e.target.checked}))} />} label="Festë kombëtare" />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel control={<Switch checked={!editHoliday.WorkingDay} onChange={e=>setEditHoliday(p=>({...p,WorkingDay:!e.target.checked}))} />} label="Ditë jo-pune" />
              </Grid>
              <ProjectScopeField kind="holiday" item={editHoliday} onChange={patch => setEditHoliday(p => ({ ...p, ...patch }))} />
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p:2, gap:1 }}>
          <Button onClick={()=>setEditHoliday(null)} variant="outlined" sx={{ borderRadius:1.5, textTransform:'none' }}>Anulo</Button>
          <Button onClick={saveHoliday} variant="contained" startIcon={<SaveIcon />} sx={{ borderRadius:1.5, textTransform:'none' }}>Ruaj</Button>
        </DialogActions>
      </Dialog>

      {/* ══ Edit blackout dialog ═══════════════════════════════════════════════ */}
      <Dialog open={!!editBlackout} onClose={() => setEditBlackout(null)} maxWidth="sm" fullWidth PaperProps={{ sx:{ borderRadius:2 } }}>
        <DialogTitle sx={{ fontWeight:700, pb:1 }}>{editBlackout?.Id===0?'Shto periudhë të bllokuar':'Edito periudhën'}</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt:2 }}>
          {editBlackout && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField size="small" fullWidth label="Emri *" value={editBlackout.Name}
                  onChange={e=>setEditBlackout(p=>({...p,Name:e.target.value}))} />
              </Grid>
              <Grid item xs={6}>
                <TextField size="small" fullWidth type="date" label="Nga data *" value={editBlackout.DateFrom}
                  onChange={e=>setEditBlackout(p=>({...p,DateFrom:e.target.value}))} InputLabelProps={{ shrink:true }} />
              </Grid>
              <Grid item xs={6}>
                <TextField size="small" fullWidth type="date" label="Deri më *" value={editBlackout.DateTo}
                  onChange={e=>setEditBlackout(p=>({...p,DateTo:e.target.value}))} InputLabelProps={{ shrink:true }} />
              </Grid>
              <Grid item xs={12}>
                <TextField select size="small" fullWidth label="Kategoria" value={editBlackout.Type}
                  onChange={e=>setEditBlackout(p=>({...p,Type:e.target.value}))}>
                  {Object.entries(BLACKOUT_TYPES).map(([v,l])=><MenuItem key={v} value={v}>{l.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField size="small" fullWidth label="Arsyeja" multiline minRows={2} value={editBlackout.Reason||''}
                  onChange={e=>setEditBlackout(p=>({...p,Reason:e.target.value}))} />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel control={<Switch checked={editBlackout.Strict} onChange={e=>setEditBlackout(p=>({...p,Strict:e.target.checked}))} />} label="Strikt (refuzo auto)" />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel control={<Switch checked={editBlackout.AppliesAll} onChange={e=>setEditBlackout(p=>({...p,AppliesAll:e.target.checked}))} />} label="Vlen për të gjithë" />
              </Grid>
              <ProjectScopeField kind="blackout" item={editBlackout} onChange={patch => setEditBlackout(p => ({ ...p, ...patch }))} />
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p:2, gap:1 }}>
          <Button onClick={()=>setEditBlackout(null)} variant="outlined" sx={{ borderRadius:1.5, textTransform:'none' }}>Anulo</Button>
          <Button onClick={saveBlackout} variant="contained" color="warning" startIcon={<SaveIcon />} sx={{ borderRadius:1.5, textTransform:'none' }}>Ruaj</Button>
        </DialogActions>
      </Dialog>

      {/* ══ Delete confirms ════════════════════════════════════════════════════ */}
      <Dialog open={!!deleteHoliday} onClose={()=>setDeleteHoliday(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Fshi pushimin zyrtar</DialogTitle>
        <DialogContent><Typography>Ky pushim do të fshihet nga kalendari.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={()=>setDeleteHoliday(null)}>Anulo</Button>
          <Button color="error" variant="contained" onClick={deleteHolidayFn}>Fshi</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteBlackout} onClose={()=>setDeleteBlackout(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Fshi periudhën e bllokuar</DialogTitle>
        <DialogContent><Typography>Kjo periudhë do të fshihet dhe pushimet do të lejohen.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={()=>setDeleteBlackout(null)}>Anulo</Button>
          <Button color="error" variant="contained" onClick={deleteBlackoutFn}>Fshi</Button>
        </DialogActions>
      </Dialog>

      {/* ══ New leave request dialog (calendar double-click) ════════════════ */}
      <Dialog open={newLeaveOpen} onClose={() => setNewLeaveOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '14px', boxShadow: '0 20px 60px rgba(0,0,0,0.12)' } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <BeachAccessIcon sx={{ color: '#3E8FA3', fontSize: 22 }} />
          Shto kërkesë pushimi
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <TextField select size="small" fullWidth label="Punëtori *"
              value={newLeaveForm.WorkerId}
              onChange={e => setNewLeaveForm(p => ({ ...p, WorkerId: e.target.value }))}>
              <MenuItem value=""><em>Zgjidh punëtorin</em></MenuItem>
              {workers.map(w => {
                const wid  = w.Id ?? w.id;
                const name = `${w.FirstName || w.Name || ''} ${w.LastName || w.Surname || ''}`.trim();
                return <MenuItem key={wid} value={String(wid)}>{name}</MenuItem>;
              })}
            </TextField>
            <TextField select size="small" fullWidth label="Lloji i pushimit *"
              value={newLeaveForm.LeaveTypeId}
              onChange={e => setNewLeaveForm(p => ({ ...p, LeaveTypeId: e.target.value }))}>
              <MenuItem value=""><em>Zgjidh llojin</em></MenuItem>
              {leaveTypes.map(t => <MenuItem key={t.Id} value={String(t.Id)}>{t.Name}</MenuItem>)}
            </TextField>
            <Stack direction="row" spacing={1.5}>
              <TextField size="small" fullWidth type="date" label="Nga *"
                value={newLeaveForm.StartDate} InputLabelProps={{ shrink: true }}
                onChange={e => setNewLeaveForm(p => ({ ...p, StartDate: e.target.value }))} />
              <TextField size="small" fullWidth type="date" label="Deri *"
                value={newLeaveForm.EndDate} InputLabelProps={{ shrink: true }}
                onChange={e => setNewLeaveForm(p => ({ ...p, EndDate: e.target.value }))} />
            </Stack>
            <TextField size="small" fullWidth multiline minRows={2}
              label="Arsyeja (opsionale)"
              value={newLeaveForm.Reason}
              onChange={e => setNewLeaveForm(p => ({ ...p, Reason: e.target.value }))}
              placeholder="p.sh. Pushim vjetor i planifikuar..." />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setNewLeaveOpen(false)}
            sx={{ textTransform: 'none', color: '#6B7280', fontWeight: 600, borderRadius: '8px' }}>Anulo</Button>
          <Button variant="contained" onClick={doCreateLeave} disabled={newLeaveSaving}
            startIcon={newLeaveSaving ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px', bgcolor: '#3E8FA3', '&:hover': { bgcolor: '#256878' }, boxShadow: 'none' }}>
            {newLeaveSaving ? 'Duke ruajtur...' : 'Shto kërkesën'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={3500} onClose={()=>setSnack('')}
        message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}
